<!--
  智慧社区数字孪生底座
  本页面作为社区管理平台的核心展示层，整合了三维实景建模与实时业务数据。
  通过数字孪生技术，实现社区治理的“一网统管”与“一图呈现”。
-->
<template>
  <!--
  WisdomCommunityScreen 核心视图区
  集成 3D 全景漫游、多维度数据可视化看板及社区资源管理。
-->
  <div
    ref="screenRef"
    class="wisdom-community-screen"
    :class="{ 'fullscreen': isFullscreen }"
  >
    <div
      class="screen-adapter"
      :style="scaleStyle"
    >
      <!-- 系统交互控制区 -->
      <div class="fullscreen-btn">
        <screenfull-component
          element-selector=".wisdom-community-screen"
          @change="handleFullscreenChange"
        />
      </div>
      <!-- 核心业务展示层 -->
      
      <div class="main-content">
        <DDModelViewer
          ref="viewer3d"
          class="map-bg"
          :style="{ pointerEvents: only3D ? 'auto' : 'none' }"
          @buildings-update="onBuildingsUpdate"
        />
      
        <!-- 视图切换控制：实现“综合态势”与“全景漫游”模式的平滑切换 -->
        <button
          class="toggle-3d-btn"
          :aria-pressed="only3D"
          :title="only3D ? '综合态势' : '全景漫游'"
          @click="toggle3D"
        >
          <svg
            v-if="!only3D"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            aria-hidden="true"
          >
            <path
              d="M4 9V4h5M20 15v5h-5M4 15v5h5M20 9V4h-5"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          <svg
            v-else
            viewBox="0 0 24 24"
            width="18"
            height="18"
            aria-hidden="true"
          >
            <path
              d="M9 4H4v5M15 20h5v-5M9 20H4v-5M15 4h5v5"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          <span style="margin-left:6px">{{ only3D ? '综合态势' : '全景漫游' }}</span>
        </button>
        <!-- 全景漫游模式下的资源导航面板 -->
        <!-- 空间资源目录树 -->
        <div
          v-show="only3D"
          class="tree-panel"
        >
          <div class="tree-panel__title">
            资源导航
          </div>
          <ul class="tree">
            <!-- 基础设施层 -->
            <li>
              <button
                class="tree-toggle"
                @click="exp.hardware = !exp.hardware"
              >
                {{ exp.hardware ? '▾' : '▸' }}
              </button>
              <span class="tree-label">基础设施</span>

              <ul v-show="exp.hardware">
                <!-- 未归类感知设备 -->
                <li
                  class="tree-leaf"
                  style="margin-top:6px;"
                  @click.stop="openLooseDevices()"
                >
                  感知设备
                </li>

                <!-- 区域级资源 -->
                <li
                  v-for="comm in COMMUNITIES"
                  :key="'hw-'+comm"
                  style="margin-top:6px;"
                >
                  <button
                    class="tree-toggle"
                    @click="hwExpanded[comm] = !hwExpanded[comm]"
                  >
                    {{ hwExpanded[comm] ? '▾' : '▸' }}
                  </button>
                  <span class="tree-label">{{ comm }}</span>

                  <!-- 建筑级资源下钻：对焦定位并关联业务数据 -->
                  <ul v-show="hwExpanded[comm]">
                    <li
                      v-for="b in (buildingsByCommunity[comm] || [])"
                      :key="'hw-b-'+b.key"
                      class="tree-leaf"
                      @click.stop="openDevicesForBuilding(comm, b)"
                    >
                      {{ b.label }}
                    </li>
                  </ul>
                </li>
              </ul>
            </li>

            <!-- 建筑空间层 -->
            <li style="margin-top:8px">
              <button
                class="tree-toggle"
                @click="exp.buildings = !exp.buildings"
              >
                {{ exp.buildings ? '▾' : '▸' }}
              </button>
              <span class="tree-label">空间建筑</span>

              <ul v-show="exp.buildings">
                <!-- 行政分区 -->
                <li
                  v-for="comm in COMMUNITIES"
                  :key="'bd-'+comm"
                  style="margin-top:6px;"
                >
                  <button
                    class="tree-toggle"
                    @click="groupExpanded[comm] = !groupExpanded[comm]"
                  >
                    {{ groupExpanded[comm] ? '▾' : '▸' }}
                  </button>
                  <span class="tree-label">{{ comm }}</span>

                  <!-- 空间对象索引 -->
                  <ul v-show="groupExpanded[comm]">
                    <li
                      v-for="b in (buildingsByCommunity[comm] || [])"
                      :key="'bd-b-'+b.key"
                      class="tree-leaf"
                      @click="handleSelect({ type:'building', name:b.key, label:b.label })"
                    >
                      {{ b.label }}
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <!-- 综合态势感知层：仅在全景模式关闭时展示 -->
        <div
          v-show="!only3D"
          class="content-layer"
        >
          <!-- 态势监控区域 -->
          <div class="left-section">
            <div class="charts-container">
              <!-- 维度1：平安社区监控 -->
              <div class="chart-container left-chart">
                <div class="section-title">
                  <img
                    src="@/assets/jingcha.png"
                    alt="logo"
                    style="height: 24px; margin-right: 8px; vertical-align: middle;"
                  >
                  <span style="font-family: 'Source Han Serif CN', 'Noto Serif CJK SC', '思源宋体', serif; letter-spacing: 2px;">平安社区</span>
                  <img
                    src="@/assets/UI.png"
                    alt="UI logo"
                    style="height: 16px; margin-left: 8px; vertical-align: middle;"
                  >
                </div>
                <a
                  class="sub-title"
                  href="http://zhsqys.tengzhou.gov.cn/jump-report/dpsj/sbgjmx"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span class="white-vertical-line" />AI感知事件
                </a>
                <div class="event-stats-container">
                  <img
                    src="@/assets/A.png"
                    alt="A logo"
                    class="event-stats-logo"
                  >
                  <div class="event-stats-text">
                    智能感知事件数
                  </div>
                  <div class="event-stats-value">
                    {{ myNumberAI }}
                  </div>
                </div>
                <div class="chart-title">
                  各类AI感知事件数目分析
                </div>
                <!-- ref 让 JS 代码可以直接获取这个 DOM 元素，渲染 ECharts 图表 -->
                <div
                  ref="aiEventChart"
                  class="chart"
                  style="width: 100%; height: 100%;"
                />
              </div>
              <!-- 图表2：协同治理事件数分析 -->
              <div class="chart-container right-chart">
                <div class="section-title">
                  <img
                    src="@/assets/shequ.png"
                    alt="community logo"
                    style="height: 24px; margin-right: 8px; vertical-align: middle;"
                  >
                  <span style="font-family: 'Source Han Serif CN', 'Noto Serif CJK SC', '思源宋体', serif; letter-spacing: 2px;">社区事件</span>
                  <img
                    src="@/assets/UI.png"
                    alt="UI logo"
                    style="height: 16px; margin-left: 8px; vertical-align: middle;"
                  >
                </div>
                <a
                  class="sub-title"
                  href="http://zhsqys.tengzhou.gov.cn/jump-report/dpsj/sspfx1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span class="white-vertical-line" />协同治理事件概况
                </a>
                <div class="cooperation-stats-row">
                  <div class="cooperation-stats-container">
                    <div class="cooperation-stats-text">
                      随手拍上报数
                    </div>
                    <div class="cooperation-stats-value">
                      {{ snapReportValue }}
                    </div>
                  </div>
                  <div class="cooperation-stats-container">
                    <div class="cooperation-stats-text">
                      随手拍待解决数
                    </div>
                    <!-- 标识随手拍解决数，ref和数据绑定名均改为snapSolvedValue，更符合业务语义 -->
                    <div class="cooperation-stats-value">
                      {{ snapSolvedValue }}
                    </div>
                  </div>
                </div>
                <div class="chart-title">
                  随手拍各类治理事件数分析
                </div>
                <div
                  ref="cooperationEventChart"
                  class="chart cooperation-chart"
                  style="width: 100%; height: 100%;"
                />
              </div>
            </div>
            <!-- 其他数据区块 ... -->
            <div class="bordered-section">
              <div class="section-title">
                <img
                  src="@/assets/jiceng.png"
                  alt="grassroots logo"
                  style="height: 24px; margin-right: 8px; vertical-align: middle;"
                >
                <span style="font-family: 'Source Han Serif CN', 'Noto Serif CJK SC', '思源宋体', serif; letter-spacing: 2px;">基层自治</span>
                <img
                  src="@/assets/UI.png"
                  alt="UI logo"
                  style="height: 16px; margin-left: 8px; vertical-align: middle;"
                >
              </div>
              <div class="flex-layout grassroots-layout with-divider">
                <div class="left-column">
                  <div class="sub-container no-sub-border no-background">
                    <a
                      class="sub-title"
                      href="http://zhsqys.tengzhou.gov.cn/jump-report/dpsj/dqjs1"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span class="white-vertical-line" />党群建设
                    </a>
                    <div
                      class="sub-content"
                      style="display: flex; flex-direction: column; flex: 1; gap: 10px;"
                    >
                      <div
                        class="top-section"
                        style="flex: 0 0 auto;"
                      >
                        <div
                          class="unified-container"
                          style="display: flex;"
                        >
                          <div style="background-color: #0e3642; padding: 10px; border-radius: 0; flex: 1;">
                            <div class="data-title">
                              <img
                                src="@/assets/dang.png"
                                alt="party logo"
                                style="height: 24px; margin-bottom: 6px; display: block; margin-left: auto; margin-right: auto;"
                              >
                            </div>
                            <div
                              class="data-title"
                              style="font-size: 12px;"
                            >
                              社区党组织数
                            </div>
                            <div
                              class="data-value"
                              style="font-size: 12px;"
                            >
                              {{ NumofCommunityPartyOrganizations }}个
                            </div>
                          </div>
                          <div style="background-color: #0f4253; padding: 10px; border-radius: 0; flex: 1.1;">
                            <div class="data-title">
                              <img
                                src="@/assets/yuan.png"
                                alt="member logo"
                                style="height: 24px; margin-bottom: 6px; display: block; margin-left: auto; margin-right: auto;"
                              >
                            </div>
                            <div
                              class="data-title"
                              style="font-size: 12px;"
                            >
                              社区党员数
                            </div>
                            <div
                              class="data-value"
                              style="font-size: 12px;"
                            >
                              {{ NumofCommunityPartyMembers }}人
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        class="activity-title-container"
                        style="height: 25px; display: flex; align-items: center; position: relative; margin-top: 25px;"
                      >
                        <img
                          src="@/assets/diwen.png"
                          alt="底纹"
                          style="position: absolute; width: 90%; height: 80%; object-fit: cover; opacity: 0.8; left: 5%; top: 10%; z-index: 0;"
                        >
                        <a
                          class="sub-title"
                          href="http://zhsqys.tengzhou.gov.cn/jump-report/dpsj/dqjs1"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <div
                            class="activity-title"
                            style="position: absolute; left: 40%; top: 50%; transform: translate(-50%, -50%); text-align: center; font-size: 12px; color: white; font-weight: 300; z-index: 1;"
                          >社区党群活动</div>
                        </a>
                      </div>
                
                      <!-- 与社区党群活动标题相连的容器 -->
                      <div
                        class="activity-content-container"
                        style="flex: 1; min-height: 150px; margin-top: 15px; background-color: transparent; border-radius: 15px; padding: 10px; border: 2px solid transparent; background-clip: padding-box; box-shadow: 0 0 10px rgba(100, 200, 255, 0.5); display: flex; justify-content: center; align-items: center;"
                      >
                        <div style="position: absolute; top: -2px; left: -2px; right: -2px; bottom: -2px; border-radius: 15px; background: linear-gradient(to right, rgba(100, 200, 255, 0.8), rgba(255, 255, 255, 0.8), rgba(100, 200, 255, 0.8)); z-index: -1;" />
                        <div
                          class="activity-content"
                          style="color: white; font-size: 11px; line-height: 1.5; text-align: center; display: flex; flex-direction: column; justify-content: space-between;"
                        >
                          <div style="display: flex; flex-direction: column; gap: 18px; position: relative;">
                            <!-- 连接线 -->
                            <div style="position: absolute; left: 13.5px; top: 16.5px; bottom: 16.5px; width: 2px; background-color: rgba(100, 200, 255, 0.8); z-index: 0;" />
                      
                            <div style="display: flex; align-items: flex-start; position: relative; z-index: 1;">
                              <img
                                src="@/assets/quan.png"
                                alt="活动图标"
                                style="width: 27px; height: 27px; margin-right: 8px; margin-top: 3px;"
                              >
                              <div>
                                <div style="text-align: left;">
                                  {{ hd1 }}
                                </div>
                                <div>{{ hd1time }}</div>
                              </div>
                            </div>
                            <div style="display: flex; align-items: flex-start; position: relative; z-index: 1;">
                              <img
                                src="@/assets/quan.png"
                                alt="活动图标"
                                style="width: 27px; height: 27px; margin-right: 8px; margin-top: 3px;"
                              >
                              <div>
                                <div style="text-align: left;">
                                  {{ hd2 }}
                                </div>
                                <div>{{ hd2time }}</div>
                              </div>
                            </div>
                            <div style="display: flex; align-items: flex-start; position: relative; z-index: 1;">
                              <img
                                src="@/assets/quan.png"
                                alt="活动图标"
                                style="width: 27px; height: 27px; margin-right: 8px; margin-top: 3px;"
                              >
                              <div>
                                <div style="text-align: left;">
                                  {{ hd3 }}
                                </div>
                                <div>{{ hd3time }}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="right-column">
                  <div
                    class="sub-container no-sub-border no-background"
                    style="flex: 1; display: flex; flex-direction: column;"
                  >
                    <a
                      class="sub-title"
                      href="http://zhsqys.tengzhou.gov.cn/jump-report/dpsj/sqys1"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span class="white-vertical-line" />社区议事
                    </a>
                    <div
                      class="sub-content"
                      style="flex: 1; position: relative; display: flex; flex-direction: column; justify-content: center;"
                    >
                      <div style="background: linear-gradient(to right, #0e3642, #1a5270); padding: 10px; border-radius: 5px; margin-bottom: 10px; display: flex; align-items: center; justify-content: space-between; position: absolute; top: 0; left: 50%; transform: translateX(-50%); width: 90%; z-index: 10;">
                        <img
                          src="@/assets/yishi.png"
                          alt="议事图标"
                          style="height: 24px; margin-right: 10px;"
                        >
                        <div style="color: white; font-size: 14px; font-weight: bold;">
                          社区议事总数
                        </div>
                        <div><span style="color: #4fc3f7; font-size: 16px; font-weight: bold;">{{ NumofCommunityDeliberationsvalue }}</span><span style="color: white; font-size: 16px; font-weight: bold;">个</span></div>
                      </div>
                      <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 50px; width: 100%;">
                        <div style="text-align: center; flex: 1;">
                          <div><span style="color: #4fc3f7; font-size: 16px; font-weight: bold;">{{ NumofCommunityrankDeliberationsvalue }}</span><span style="color: white; font-size: 16px; font-weight: bold;">件</span></div>
                          <div style="color: white; font-size: 14px;">
                            社区级议事数
                          </div>
                        </div>
                        <div style="text-align: center;">
                          <img
                            src="@/assets/bing.png"
                            alt="logo"
                            style="width: 50px; height: 50px;"
                          >
                        </div>
                        <div style="text-align: center; flex: 1;">
                          <div><span style="color: #4fc3f7; font-size: 16px; font-weight: bold;">{{ NumofsmallCommunityrankDeliberationsvalue }}</span><span style="color: white; font-size: 16px; font-weight: bold;">件</span></div>
                          <div style="color: white; font-size: 14px;">
                            小区级议事数
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="sub-container no-sub-border no-background"
                    style="flex: 1; display: flex; flex-direction: column;"
                  >
                    <a
                      class="sub-title"
                      href="http://zhsqys.tengzhou.gov.cn/jump-report/dpsj/zzhd1"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span class="white-vertical-line" />社区活动
                    </a>
                    <div
                      class="sub-content"
                      style="flex: 1; display: flex; flex-direction: column;"
                    >
                      <div style="text-align: center; position: relative;">
                        <div
                          class="chart-title"
                          style="margin-bottom: 5px;"
                        >
                          各社会组织活动数分析
                        </div>
                      </div>
                      <div
                        ref="socialOrgChart"
                        class="chart"
                        style="width: 100%; flex: 1; min-height: 100px;"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>



          <!-- 右侧区域 -->
          <div
            class="right-section"
            style="display: flex; flex-direction: column;"
          >
            <div
              class="bordered-section"
              style="flex: 1; overflow: hidden;"
            >
              <div class="section-title">
                <img
                  src="@/assets/an.png"
                  alt="logo"
                  style="height: 24px; margin-right: 8px; vertical-align: middle;"
                ><span style="font-family: 'Source Han Serif CN', 'Noto Serif CJK SC', '思源宋体', serif; letter-spacing: 2px;">社区档案</span><img
                  src="@/assets/UI.png"
                  alt="UI logo"
                  style="height: 16px; margin-left: 8px; vertical-align: middle;"
                >
              </div>
              <div class="sub-titles-container">
                <div class="vertical-box">
                  <a
                    class="sub-title"
                    href="http://zhsqys.tengzhou.gov.cn/jump-report/dpsj/zzfx1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span class="white-vertical-line" />总人口数
                  </a>
                  <div
                    class="gender-number"
                    style=" margin: 0 auto 20px auto; /* 水平居中 */"
                  >
                    {{ TotalPopulation }}人
                  </div>
                  <div class="sub-title">
                    <span class="white-vertical-line" />男女人口比例
                  </div>
                  <div class="gender-data-container">
                    <div class="gender-icons-container">
                      <div class="gender-icon-with-number">
                        <img
                          src="@/assets/nan.png"
                          alt="男性图标"
                          class="gender-icon"
                        >
                        <span
                          class="gender-number"
                          style="width: 80px;"
                        >{{ MalePopulation }}</span>
                      </div>
                      <div class="gender-icon-with-number">
                        <img
                          src="@/assets/nv.png"
                          alt="女性图标"
                          class="gender-icon"
                        >
                        <span
                          class="gender-number"
                          style="width: 80px;"
                        >{{ FemalePopulation }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="vertical-box">
                  <div class="sub-title">
                    <span class="white-vertical-line" />重点人群分析
                  </div>
                  <!-- 重点人群分析圆环图 -->
                  <div
                    ref="keyPopulationChart"
                    class="key-population-chart"
                  />
                </div>
              </div>
            </div>

            <div class="service-container with-divider">
              <div class="service-left">
                <!-- 内部竖直排列的容器 -->
                <div style="display: flex; flex-direction: column; gap: 0px;">
                  <!-- 在这里添加你的内容，每个子元素会竖直排列 -->
                  <div class="section-title">
                    <img
                      src="@/assets/shequ.png"
                      alt="logo"
                      style="height: 30px; margin-right: 8px; vertical-align: middle;"
                    ><span style="font-family: 'Source Han Serif CN', 'Noto Serif CJK SC', '思源宋体', serif; letter-spacing: 2px;">民生服务</span><img
                      src="@/assets/UI.png"
                      alt="UI logo"
                      style="height: 16px; margin-left: 8px; vertical-align: middle;"
                    >
                  </div>
                  <!-- <div class="service-subtitle"><span class="white-vertical-line"></span>医疗服务</div> -->
                  <a
                    class="sub-title"
                    href="http://zhsqys.tengzhou.gov.cn/jump-report/dpsj/ylsjfx"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span class="white-vertical-line" />医疗服务
                  </a>
                  <div class="service-total-title">
                    <img
                      src="@/assets/yishi.png"
                      alt="议事"
                      style="height: 18px; margin-right: 8px; vertical-align: middle;"
                    >医师数量<span class="service-count">{{ numofdctor }}</span><span class="service-unit">件</span>
                  </div>
                  <div class="service-pending-title">
                    <img
                      src="@/assets/jing.png"
                      alt="警告"
                      style="height: 18px; margin-right: 8px; vertical-align: middle;"
                    >线上预约数量<span class="service-count pending-count">{{ numofonlineappts }}</span><span class="service-unit">件</span>
                  </div>
                  <a
                    class="sub-title"
                    href="http://zhsqys.tengzhou.gov.cn/jump-report/dpsj/jyfwfx1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span class="white-vertical-line" />就业服务
                  </a>
                  <div class="chart-title">
                    就业服务组织数分析
                  </div>
                  <div
                    ref="mediationEventPieChart"
                    class="mediation-event-pie-chart "
                  />
                  <!-- ...可以继续添加更多内容 ... -->
                </div>
                
                <!-- 左侧内容 -->
              </div>
              <div class="service-right">
                <div class="section-title">
                  <img
                    src="@/assets/quan.png"
                    alt="logo"
                    style="height: 30px; margin-right: 8px; vertical-align: middle;"
                  ><span style="font-family: 'Source Han Serif CN', 'Noto Serif CJK SC', '思源宋体', serif; letter-spacing: 2px;">周边服务</span><img
                    src="@/assets/UI.png"
                    alt="UI logo"
                    style="height: 16px; margin-left: 8px; vertical-align: middle;"
                  >
                </div>
                <!-- <div class="service-subtitle"><span class="white-vertical-line"></span>金点子</div> -->
                <a
                  class="sub-title"
                  href="http://zhsqys.tengzhou.gov.cn/jump-report/dpsj/jdzfx1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span class="white-vertical-line" />周边服务
                </a>
                <div class="service-total-title">
                  <img
                    src="@/assets/yishi.png"
                    alt="议事"
                    style="height: 18px; margin-right: 8px; vertical-align: middle;"
                  >周边商家数<span class="service-count total-count">{{ goldenIdeasTotal }}</span><span class="service-unit">个</span>
                </div>
                <!-- <div class="service-pending-title"><img src="@/assets/jinggao.png" alt="警告" style="height: 18px; margin-right: 8px; vertical-align: middle;">金点子未完成数<span class="service-count pending-right-count">{{goldenIdeasPending}}</span><span class="service-unit">个</span></div> -->
                <div class="chart-title">
                  周边服务类型数量分析
                </div>
                <!-- <div ref="propertyRepairChart" class="chart" style="width: 100%; height: 350px; margin-top: 5px;"></div> -->
                <div
                  ref="goldenIdeaPieChart"
                  class="golden-idea-pie-chart "
                />
                <!-- 右侧内容 -->
              </div>
            </div>


            <div class="bordered-section team-section">
              <!-- <div class="section-title"><img src="@/assets/cheng.png" alt="cheng logo" style="height: 24px; margin-right: 8px; vertical-align: middle;"><span style="font-family: 'Source Han Serif CN', 'Noto Serif CJK SC', '思源宋体', serif; letter-spacing: 2px;">基层队伍</span><img src="@/assets/UI.png" alt="UI logo" style="height: 16px; margin-left: 8px; vertical-align: middle;"></div> -->
              <div class="section-title">
                <img
                  src="@/assets/jiceng.png"
                  alt="cheng logo"
                  style="height: 24px; margin-right: 8px; vertical-align: middle;"
                >
                <a
                  class="label-link"
                  href="http://60.214.100.238:6231/sjgl/a/ggzyIndexMain"
                  target="_blank" 
                  rel="noopener noreferrer"
                >基层队伍</a>
                <img
                  src="@/assets/UI.png"
                  alt="UI logo"
                  style="height: 16px; margin-left: 8px; vertical-align: middle;"
                >
              </div> 
              <div class="stats-grid">
                <div
                  class="stat-item stat-item-top-first"
                  style="margin-top: -15px;"
                >
                  <div
                    class="stat-content"
                    style="display: flex; align-items: center; gap: 8px; margin-left: 1px;"
                  >
                    <div
                      class="stat-icon stat-icon-top"
                      style="width: 40px; height: 40px; border-radius: 50%; background-color: #17dff0; display: flex; align-items: center; justify-content: center; margin-top: -25px;"
                    >
                      <img
                        src="@/assets/jing.png"
                        alt="网格图标"
                        style="width: 20px; height: 20px;"
                      >
                    </div>
                    <div class="stat-text stat-text-top-first">
                      <div class="stat-title">
                        社区网格数
                      </div>
                      <div class="stat-value">
                        <span style="color: #17dff0;">{{ communityGridCount }}</span><span style="color: white;">个</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  class="stat-item stat-item-top-first"
                  style="margin-top: -15px;"
                >
                  <div
                    class="stat-content"
                    style="display: flex; align-items: center; gap: 8px; margin-left: 1px;"
                  >
                    <div
                      class="stat-icon stat-icon-top"
                      style="width: 40px; height: 40px; border-radius: 50%; background-color: #2476de; display: flex; align-items: center; justify-content: center; margin-top: -25px;"
                    >
                      <img
                        src="@/assets/nan.png"
                        alt="人员图标"
                        style="width: 20px; height: 20px;"
                      >
                    </div>
                    <div class="stat-text stat-text-top-first">
                      <div class="stat-title">
                        社区网格员数
                      </div>
                      <div class="stat-value">
                        <span style="color: #2476de;">{{ communityGridMemberCount }}</span><span style="color: white;">个</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  class="stat-item stat-item-top-first"
                  style="margin-top: -25px;"
                >
                  <div
                    class="stat-content"
                    style="display: flex; align-items: center; gap: 8px; margin-left: 15px;"
                  >
                    <div
                      class="stat-icon"
                      style="width: 40px; height: 40px; border-radius: 50%; background-color: #3c6b8e; display: flex; align-items: center; justify-content: center; margin-top: -20px;"
                    >
                      <img
                        src="@/assets/dang.png"
                        alt="事件图标"
                        style="width: 20px; height: 20px;"
                      >
                    </div>
                    <div class="stat-text">
                      <div class="stat-title">
                        待处理事件数
                      </div>
                      <div class="stat-value">
                        <span style="color: #3c6b8e;">15</span><span style="color: white;">件</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  class="stat-item stat-item-top-first"
                  style="margin-top: -25px;"
                >
                  <div
                    class="stat-content"
                    style="display: flex; align-items: center; gap: 8px; margin-left: 15px;"
                  >
                    <div
                      class="stat-icon"
                      style="width: 40px; height: 40px; border-radius: 50%; background-color: #00FF7F; display: flex; align-items: center; justify-content: center; margin-top: -20px;"
                    >
                      <img
                        src="@/assets/UI.png"
                        alt="满意度图标"
                        style="width: 20px; height: 20px;"
                      >
                    </div>
                    <div class="stat-text">
                      <div class="stat-title">
                        事件解决满意度
                      </div>
                      <div class="stat-value">
                        <span style="color: #00FF7F;">92%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import * as echarts from 'echarts'
import DDModelViewer from '@/components/DDModelViewer.vue';
import axios from 'axios'  // ← 在顶部加这个

// 2) 引入映射表
import buildingNameMap from '@/config/building-map'


export default {
  name: 'WisdomCommunityScreen',
  components: { DDModelViewer },  // 一定要注册
  // setup() 是 Vue 3 组合式 API 的入口函数，用于声明响应式数据、方法等，并将其暴露给模板使用。

  setup() {
    // 定义响应式变量 isFullscreen，表示当前是否全屏。ref 是 Vue 3 的响应式 API。
    const isFullscreen = ref(false)
    const screenRef = ref(null)

    // 大屏自适应缩放逻辑
    const designWidth = 1920
    const designHeight = 1080
    const scaleStyle = ref({
      width: `${designWidth}px`,
      height: `${designHeight}px`,
      transform: 'scale(1)',
      transformOrigin: '0 0',
      position: 'absolute',
      left: '0',
      top: '0',
      transition: 'transform 0.3s ease'
    })

    const updateScale = () => {
      // 获取当前容器的实际宽高，而不是 window 的宽高
      // 这样可以避开顶部 Header 占据的空间
      if (!screenRef.value) return
      
      const ww = screenRef.value.clientWidth
      const wh = screenRef.value.clientHeight
      
      if (ww === 0 || wh === 0) return

      const scaleX = ww / designWidth
      const scaleY = wh / designHeight
      scaleStyle.value.transform = `scale(${scaleX}, ${scaleY})`
    }

    onMounted(() => {
      nextTick(() => {
        updateScale()
      })
      window.addEventListener('resize', updateScale)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', updateScale)
    })
    
    // 定义处理全屏状态变化的函数。参数 fullscreen 表示新的全屏状态。
    const handleFullscreenChange = (fullscreen) => {
      // 更新 isFullscreen 的值
      isFullscreen.value = fullscreen
      
      // 触发大屏缩放重新计算
      nextTick(() => {
        updateScale()
      })

      // 当全屏状态变化时，遍历所有已注册的图表，调用 resize 方法以适应新尺寸
      Object.values(charts.value).forEach(chart => {
        if (chart) {
          // 使用 setTimeout 延迟 100ms，确保 DOM 更新后再调整图表大小
          setTimeout(() => {
            chart.resize()
          }, 100)
        }
      })
    }

    // 以下是各个图表的 DOM 引用，初始为 null，后续会绑定到具体的 DOM 元素
    const aiEventChart = ref(null)              // AI感知事件数目分析图表
    const cooperationEventChart = ref(null)     // 协同治理事件数分析图表
    const socialOrgChart = ref(null)            // 社会组织活动数分析图表
    const populationFlowChart = ref(null)       // 人口流动分析图表
    const keyPopulationChart = ref(null)        // 重点人口分析图表
    const mediationChart = ref(null)            // 调解事件分析图表
    const propertyRepairChart = ref(null)       // 物业报修分析图表
    const mediationEventPieChart =ref(null)             //各类矛盾调解事件数分析饼图
    const goldenIdeaPieChart = ref(null)      //金点子上报类型分析饼图

    const myNumberAI = ref(0)          // AI感知事件数量
    const snapReportValue = ref(0)          // 随手拍上报数
    const snapSolvedValue = ref(0)   // 随手拍待解决数
    const TotalPopulation = ref(0)   // 总人口数
    const FemalePopulation = ref(0)   // 女性人口数
    const MalePopulation = ref(0)     // 男性人口数
    const NumofCommunityPartyOrganizations = ref(0)   // 社区党组织数
    const NumofCommunityPartyMembers = ref(0)         // 社区党员数
    const NumofsmallCommunityrankDeliberationsvalue = ref(0) // 小区级议事数
    const NumofCommunityrankDeliberationsvalue = ref(0)      // 社区级议事数
    const NumofCommunityDeliberationsvalue = ref(0)          // 社区议事总数
    const numofdctor = ref(0)          // 医师数量
    const numofonlineappts = ref(0)    // 线上预约数量
    const goldenIdeasTotal = ref(0)      // 金点子总数
    const goldenIdeasPending = ref(0)    // 金点子未完成数
    const communityGridCount = ref(0)       // 社区网格数
    const communityGridMemberCount = ref(0) // 社区网格员数
    const hd1 = ref('') // 活动1标题
    const hd1time = ref('') // 活动1时间
    const hd2 = ref('') // 活动2标题
    const hd2time = ref('') // 活动2时间
    const hd3 = ref('') // 活动3标题
    const hd3time = ref('') // 活动3时间

    // charts 用于存放所有图表实例，便于统一管理和操作
    const charts = ref({})

  // 初始化图表的通用函数
  // chartRef: 图表的 DOM 引用
  // option: ECharts 的配置项
  // 这里使用了箭头函数（Arrow Function）语法：
  // (参数1, 参数2) => { ... }，是 ES6 的简洁函数写法，不会绑定自己的 this，适合用来写回调和匿名函数。
  const initChart = (chartRef, option) => {
      // 如果 DOM 元素还未挂载，直接返回 null
      if (!chartRef.value) return null

      // 初始化 ECharts 实例
      const chartInstance = echarts.init(chartRef.value)
      // 设置图表的配置项
      chartInstance.setOption(option)

      // 监听窗口 resize 事件，窗口大小变化时自动调整图表尺寸
      window.addEventListener('resize', () => {
        chartInstance.resize()
      })

      // 返回图表实例，便于后续管理
      return chartInstance
    }

    // 图表动画的统一配置，便于多个图表复用
    const chartAnimationConfig = {
      animation: true,                        // 开启动画
      animationDuration: 1500,                // 初始动画时长 1500ms
      animationEasing: 'elasticOut',          // 动画缓动函数
      animationDelay: (idx) => idx * 100,     // 每个系列的动画延迟，idx 为数据索引
      animationDurationUpdate: 1000           // 数据更新时的动画时长
    }

  // 组件挂载后执行的生命周期钩子
  // 组件挂载：指组件实例被创建并插入到页面（DOM）上的过程
  // 生命周期钩子：Vue 提供的特殊函数，在组件创建、挂载、更新、销毁等阶段自动调用
  // onMounted 就是在组件挂载到页面后自动执行的钩子函数
  onMounted(async() => {
          // AI感知事件数目分析图表
      // 1. 请求数据
      // const res = await axios.post('http://127.0.0.1:4523/m2/7183632-6908477-default/356100801', {})
      // console.log("接口返回：", res.data)
      // ...existing code...
      // const res_minsheng = await axios.post('https://jqej.zysign.net/api/mini/ys/big/screen/data',{"query_code":"fadfadfffffffffffffff","schema_code":"fadfadfffffffffffffff","page":1})
      // console.log("民生接口返回：", res_minsheng.data)

      // 循环分页获取，直到下一页无数据为止
      const url = 'https://jqej.zysign.net/api/mini/ys/big/screen/data'
      const queryPayload = {
        query_code: "fadfadfffffffffffffff",
        schema_code: "fadfadfffffffffffffff",
        // 其它固定参数如果有，放这里
      }

      const allPages = []
      let page = 1
      const pageSizeHint = 50 // 后端默认可能为50，作为判断结束的参考
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          const resp = await axios.post(url, { ...queryPayload, page })
          const pageData = resp?.data?.data ?? resp?.data ?? []
          console.log(`[民生接口] page=${page} 返回条数:`, Array.isArray(pageData) ? pageData.length : 0)
          if (!Array.isArray(pageData) || pageData.length === 0) break
          allPages.push(...pageData)
          // 如果返回条数少于我们期望的 pageSizeHint，判断为最后一页（兜底）
          if (pageData.length < pageSizeHint) break
          page++
          // 可选：短暂停顿，防止触发接口限流
          await new Promise(r => setTimeout(r, 200))
        } catch (err) {
          console.warn(`[民生接口] 第 ${page} 页请求失败：`, err)
          break
        }
      }

      console.log("民生接口全部数据：", allPages)
      const all = allPages
// ...existing code...


// 循环分页获取，直到下一页无数据为止
      const url_sqdqhd = 'https://jqej.zysign.net/api/mini/ys/group/building/activities'
      const queryPayload_sqdqhd = {
        query_code: "jqblsq_djhd",
        schema_code: "jqblsq_djhd",
        // 其它固定参数如果有，放这里
      }

      const allPages_sqdqhd = []
      let page_sqdqhd = 1
      const pageSizeHint_sqdqhd = 50 // 后端默认可能为50，作为判断结束的参考
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          const resp = await axios.post(url_sqdqhd, { ...queryPayload_sqdqhd, page: page_sqdqhd })
          const pageData = resp?.data?.data ?? resp?.data ?? []
          console.log(`[民生接口] page=${page_sqdqhd} 返回条数:`, Array.isArray(pageData) ? pageData.length : 0)
          if (!Array.isArray(pageData) || pageData.length === 0) break
          allPages_sqdqhd.push(...pageData)
          // 如果返回条数少于我们期望的 pageSizeHint，判断为最后一页（兜底）
          if (pageData.length < pageSizeHint_sqdqhd) break
          page_sqdqhd++
          // 可选：短暂停顿，防止触发接口限流
          await new Promise(r => setTimeout(r, 200))
        } catch (err) {
          console.warn(`[民生接口] 第 ${page_sqdqhd} 页请求失败：`, err)
          break
        }
      }

      console.log("社区党群接口全部数据：", allPages_sqdqhd)
      const all_sqdqhd = allPages_sqdqhd
            // // 从接口响应中获取原始数据对象，若不存在则为 {}，用于后续数据提取
      // const raw = res.data?.data?.[0] || {}
///////////////////////////////////////////////////////////////////////////////////////////////////////

//基层自治卡片的社区党群活动

      // 4) 按 Level_1_Type 分组，累计“已完成 / 未完成”
      const hdnr_sqdqhd = []
      const hdsj_sqdqhd = []
      for (let i=0; i<3; i++) {
        const r = all_sqdqhd[i];
        const type = (r?.Activity_Content ?? '未知类型').trim()//活动名字
        hdnr_sqdqhd[i] = type
        
        const status = (r?.Activity_Date ?? '').trim() //时间
        hdsj_sqdqhd[i] = status
        // const val = Number(r?.Numerical_Value ?? 0)
      }
      hd1.value = hdnr_sqdqhd[0]
      hd1time.value = hdsj_sqdqhd[0]
      hd2.value = hdnr_sqdqhd[1]
      hd2time.value = hdsj_sqdqhd[1]
      hd3.value = hdnr_sqdqhd[2]
      hd3time.value = hdsj_sqdqhd[2]

//////////////从民生接口响应中获取AI 感知事件数目////////////////////////////////////////////
      
      // 2) 民生数据结构中取到一个“数组形式”的原始数据
      // 语法说明：
      // 1. res_minsheng?.data?.data：可选链，安全获取嵌套属性，防止报错。
      // 2. ?? 空值合并运算符，左侧为null/undefined时取右侧。
      // 3. || 逻辑或，前面为假值时取空数组，保证all始终为数组。
      // 业务含义：优先获取res_minsheng.data.data，如果没有则取res_minsheng.data，如果还没有则用空数组，确保all一定是数组。
      // const all = (res_minsheng?.data?.data ?? res_minsheng?.data ?? []) || []

      // 3) 只要 Belonging = "AI感知事件" 的
      // 语法说明：
      // 1. filter是数组方法，筛选符合条件的元素，返回新数组。
      // 2. r => r?.Belonging?.trim() === 'AI感知事件' 是箭头函数，判断每一项的Belonging字段（去除首尾空格后）是否等于'AI感知事件'。
      // 3. ?.是可选链，防止r或Belonging为null时报错。
      // 业务含义：筛选出所有属于“AI感知事件”的数据行。
      //.trim()：去除字符串首尾空格，保证比较时不受空格影响。
      const aiRows = all.filter(r => r?.Belonging?.trim() === 'AI感知事件')

      // 4) 按 Level_1_Type 分组，累计“已完成 / 未完成”
      const byType = new Map()
      for (const r of aiRows) {
        const type = (r?.Level_1_Type ?? '未知类型').trim()
        
        const status = (r?.Level_2_Type ?? '').trim() // "已完成" / "未完成"
        const val = Number(r?.Numerical_Value ?? 0)
        
        if (type === '智能感知事件数') {
          myNumberAI.value = val
          continue
        }
  

        if ((!byType.has(type)) && status != '') byType.set(type, { completed: 0, uncompleted: 0 }) //筛选条件：必须有状态2才分组
        // 语法说明：
        // byType 是一个 Map（映射表），get(type) 用于根据键 type 查找对应的分组对象。
        // 业务含义：根据当前类型 type，获取分组统计对象，方便后续累加统计。
        // 说明：bucket 是 Map 里存储对象的引用（不是副本），修改 bucket 的属性会直接同步到 Map 里，无需重新 set。
        const bucket = byType.get(type)
        if (status === '已完成') bucket.completed += val
        else if (status === '未完成') bucket.uncompleted += val
      }


      // 5) 取前两类（后端当前只有两组就正好两条；多了也只画两条）
      // 语法说明：
      // byType 是一个映射表（Map），用于存储“键值对”关系。每个类型（键）对应一个分组对象（值），可以通过键快速查找和统计。
      // byType.keys() 返回 Map 里的所有 key（类型），是一个迭代器。
      // Array.from(...) 把迭代器转为数组。
      // 业务含义：收集所有分组类型，方便后续遍历或处理。
      const allTypes = Array.from(byType.keys())
      // // 语法说明：
      // // allTypes 是一个类型数组，slice(0, 2) 表示从索引0开始，截取到索引2（不包括2），即取前两个类型。
      // // 业务含义：只取前两个类型作为事件类型，组成新数组 eventTypes。
      // const eventTypes = allTypes.slice(0, 2)

      // 语法说明：
      // allTypes 是类型数组，map 对每个类型 t 执行一次回调。
      // byType.get(t) 从映射表获取分组对象，?.completed 安全获取已完成数量，?? 0 保证没有数据时为0。
      // 业务含义：统计所有类型的已完成数量，组成数组 completed。
      const completed = allTypes.map(t => byType.get(t)?.completed ?? 0)
      // 同理，统计所有类型的未完成数量，组成数组 uncompleted。
      const uncompleted = allTypes.map(t => byType.get(t)?.uncompleted ?? 0)

      // // 6) 动态估计 x 轴最大值：取两堆叠之和的最大，再加 20% 余量
      // const sums = allTypes.map((_, i) => (completed[i] || 0) + (uncompleted[i] || 0))
      // const xMax = Math.max(10, Math.max(...sums, 0)) * 1.2

  ///////////////从民生接口响应中获取协同治理事件概况///////////////////////////////////////////////////////////////////////

      const aiRows_xt = all.filter(r => r?.Belonging?.trim() === '协同治理事件')


      const byType_xt = new Map()
      for (const r of aiRows_xt) {
        const type_xt = (r?.Level_1_Type ?? '未知类型').trim()
        const status_xt = (r?.Level_2_Type ?? '').trim()
        const val_xt = Number(r?.Numerical_Value ?? 0)

        if (type_xt === '随手拍'&& status_xt==='上报数') {
          snapReportValue.value = val_xt
          console.log("type_xt:", type_xt, "status_xt:", status_xt, "snapReportValue.value:", snapReportValue.value)
          continue
        }
        if (type_xt === '随手拍'&& status_xt==='待解决数') {
          snapSolvedValue.value = val_xt
          console.log("type_xt:", type_xt, "status_xt:", status_xt, "snapSolvedValue.value:", snapSolvedValue.value)
          continue
        }

      

        if ((!byType_xt.has(status_xt)) && status_xt != '上报数'&& status_xt != '待解决数') byType_xt.set(status_xt, { num_xt: 0}) //筛选条件：必须有状态2才分组
        const bucket_xt = byType_xt.get(status_xt)
        bucket_xt.num_xt += val_xt
      }
      
      // 语法说明：
      // 步骤详细拆解：
      // 1. byType_xt.keys()：获取Map里所有的key（类型名），结果是一个“迭代器”，不是数组。
      // 2. Array.from(...)：把迭代器变成真正的数组，比如['高空抛物', '垃圾清理', ...]。
      // 3. .map(...)：对数组里的每个类型名都执行一次回调函数。
      // 4. 回调函数 name => ({ name, icon: 'circle' })：把每个类型名变成一个对象，包含name字段和icon字段。
      // 5. 最终得到一个新数组，每个元素都是{name: 类型名, icon: 'circle'}。
      // 业务含义：这个数组可以直接用于图表的图例、标签等地方。
      const nameIconArr = Array.from(byType_xt.keys()).map(name => ({
        name,
        icon: 'circle'
      }))

      const keys = Array.from(byType_xt.keys())
      const valueNameArr = keys.map((name, idx) => ({
        value: byType_xt.get(name).num_xt,
        name,
        itemStyle: { color: `hsl(${(idx * 360) / keys.length}, 70%, 50%)` }
      }))
      console.log('nameIconArr:', nameIconArr)
      console.log('valueNameArr:', valueNameArr)
      // const allTypes_xt = Array.from(byType_xt.keys())
      // const firstValue = byType_xt.get(firstType)                 // 取第一个key对应的值对象
      // const num_xt = firstValue.num_xt 

   

  //////////////////从民生接口响应中获取社区档案卡片///////////////////////////////////////////////////////////////////////////////////
      const aiRows_sq = all.filter(r => r?.Belonging?.trim() === '人员信息')


      const byType_sq = new Map()
      for (const r of aiRows_sq) {
        const type_sq = (r?.Level_1_Type ?? '未知类型').trim()
        // const status_sq = (r?.Level_2_Type ?? '').trim()
        const val_sq = Number(r?.Numerical_Value ?? 0)

        if (type_sq ==='总人口') {
          TotalPopulation.value = val_sq
          console.log("type_sq:", type_sq,  "TotalPopulation.value:", TotalPopulation.value)
          continue
        }
        if (type_sq ==='男') {
          MalePopulation.value = val_sq
          console.log("type_sq:", type_sq,  "MalePopulation.value:", MalePopulation.value)
          continue
        }
        if (type_sq ==='女') {
          FemalePopulation.value = val_sq
          console.log("type_sq:", type_sq,  "FemalePopulation.value:", FemalePopulation.value)
          continue
        }
      

        if ((!byType_sq.has(type_sq)) && type_sq != '总人口'&& type_sq != '男'&& type_sq != '女') byType_sq.set(type_sq, { num_sq: 0}) //筛选条件：必须有状态2才分组
        const bucket_sq = byType_sq.get(type_sq)
        bucket_sq.num_sq += val_sq
      }
      
      // 语法说明：
      // 步骤详细拆解：
      // 1. byType_xt.keys()：获取Map里所有的key（类型名），结果是一个“迭代器”，不是数组。
      // 2. Array.from(...)：把迭代器变成真正的数组，比如['高空抛物', '垃圾清理', ...]。
      // 3. .map(...)：对数组里的每个类型名都执行一次回调函数。
      // 4. 回调函数 name => ({ name, icon: 'circle' })：把每个类型名变成一个对象，包含name字段和icon字段。
      // 5. 最终得到一个新数组，每个元素都是{name: 类型名, icon: 'circle'}。
      // 业务含义：这个数组可以直接用于图表的图例、标签等地方。
      const nameIconArr_sq = Array.from(byType_sq.keys()).map(name => ({
        name,
        icon: 'circle'
      }))

      const keys_sq = Array.from(byType_sq.keys())
      const valueNameArr_sq = keys_sq.map((name, idx) => ({
        value: byType_sq.get(name).num_sq,
        name,
        itemStyle: { color: `hsl(${(idx * 360) / keys_sq.length}, 70%, 50%)` }
      }))
      console.log('nameIconArr:', nameIconArr_sq)
      console.log('valueNameArr:', valueNameArr_sq)
      // const allTypes_xt = Array.from(byType_xt.keys())
      // const firstValue = byType_xt.get(firstType)                 // 取第一个key对应的值对象
      // const num_xt = firstValue.num_xt 

  ///////////////////////从民生接口响应中获取基层自治卡片////////////////////////////////////////////////////////////////////////////////////

      const aiRows_jc = all.filter(r => r?.Belonging?.trim() === '党群建设')



      const byType_jc = new Map()
      for (const r of aiRows_jc) {
        const type_jc = (r?.Level_1_Type ?? '未知类型').trim()
        // const status_jc = (r?.Level_2_Type ?? '').trim()
        const val_jc = Number(r?.Numerical_Value ?? 0)

        if (type_jc ==='社区党员数') {
          NumofCommunityPartyMembers.value = val_jc
          console.log("type_jc:", type_jc,  "NumofCommunityPartyMembers.value:", NumofCommunityPartyMembers.value)
          continue
        }
        if (type_jc ==='社区党组织数') {
          NumofCommunityPartyOrganizations.value = val_jc
          console.log("type_jc:", type_jc,  "NumofCommunityPartyOrganizations.value:", NumofCommunityPartyOrganizations.value)
          continue
        }
        

        // if ((!byType_jc.has(type_jc)) && type_jc != '总人口'&& type_jc != '男'&& type_jc != '女') byType_jc.set(type_jc, { num_jc: 0}) //筛选条件：必须有状态2才分组
        // const bucket_jc = byType_jc.get(type_jc)
        // bucket_jc.num_jc += val_jc
      }
      
      // 语法说明：
      // 步骤详细拆解：
      // 1. byType_xt.keys()：获取Map里所有的key（类型名），结果是一个“迭代器”，不是数组。
      // 2. Array.from(...)：把迭代器变成真正的数组，比如['高空抛物', '垃圾清理', ...]。
      // 3. .map(...)：对数组里的每个类型名都执行一次回调函数。
      // 4. 回调函数 name => ({ name, icon: 'circle' })：把每个类型名变成一个对象，包含name字段和icon字段。
      // 5. 最终得到一个新数组，每个元素都是{name: 类型名, icon: 'circle'}。
      // 业务含义：这个数组可以直接用于图表的图例、标签等地方。
      const nameIconArr_jc = Array.from(byType_jc.keys()).map(name => ({
        name,
        icon: 'circle'
      }))

      const keys_jc = Array.from(byType_jc.keys())
      const valueNameArr_jc = keys_jc.map((name, idx) => ({
        value: byType_jc.get(name).num_jc,
        name,
        itemStyle: { color: `hsl(${(idx * 360) / keys_jc.length}, 70%, 50%)` }
      }))
      console.log('nameIconArr:', nameIconArr_jc)
      console.log('valueNameArr:', valueNameArr_jc)
      // const allTypes_xt = Array.from(byType_xt.keys())
      // const firstValue = byType_xt.get(firstType)                 // 取第一个key对应的值对象
      // const num_xt = firstValue.num_xt 
  //////////////////////////从民生接口响应中获取社区议事////////////////////////////////////////////////////////////////////
      const aiRows_ys = all.filter(r => r?.Belonging?.trim() === '社区议事')
      const aiRows_zz = all.filter(r => r?.Belonging?.trim() === '社会组织')



      // const byType_ys = new Map()
      const byType_zz = new Map()
      for (const r of aiRows_ys) {
        const type_ys = (r?.Level_1_Type ?? '未知类型').trim()
        // const status_ys = (r?.Level_2_Type ?? '').trim()
        const val_ys = Number(r?.Numerical_Value ?? 0)

        if (type_ys ==='小区级议事') {
          NumofsmallCommunityrankDeliberationsvalue.value = val_ys
          console.log("type_ys:", type_ys,  "NumofsmallCommunityrankDeliberationsvalue.value:", NumofsmallCommunityrankDeliberationsvalue.value)
          continue
        }
        if (type_ys ==='社区级议事') {
          NumofCommunityrankDeliberationsvalue.value = val_ys
          console.log("type_ys:", type_ys,  "NumofCommunityrankDeliberationsvalue.value:", NumofCommunityrankDeliberationsvalue.value)
          continue
        }
        if (type_ys ==='议事总数') {
          NumofCommunityDeliberationsvalue.value = val_ys
          console.log("type_ys:", type_ys,  "NumofCommunityDeliberationsvalue.value:", NumofCommunityDeliberationsvalue.value)
          continue}

        // if ((!byType_ys.has(type_ys)) && type_ys != '小区级议事'&& type_ys != '社区级议事'&& type_ys != '议事总数') byType_ys.set(type_ys, { num_ys: 0}) //筛选条件：必须有状态2才分组
        // const bucket_ys = byType_ys.get(type_ys)
        // bucket_ys.num_ys += val_ys
      
    }
    for (const r of aiRows_zz) {
        const type_zz = (r?.Level_1_Type ?? '未知类型').trim()
        // const status_ys = (r?.Level_2_Type ?? '').trim()
        const val_zz = Number(r?.Numerical_Value ?? 0)

        if ((!byType_zz.has(type_zz)) ) byType_zz.set(type_zz, { num_zz: 0}) //筛选条件：必须有状态2才分组
        const bucket_zz = byType_zz.get(type_zz)
        bucket_zz.num_zz += val_zz
      
    }

    const nameIconArr_zz = Array.from(byType_zz.keys()).map(name => ({
        name,
        icon: 'circle'
      }))

      const keys_zz = Array.from(byType_zz.keys())
      const valueNameArr_zz = keys_zz.map((name, idx) => ({
        value: byType_zz.get(name).num_zz,
        name,
        itemStyle: { color: `hsl(${(idx * 360) / keys_zz.length}, 70%, 50%)` }
      }))
      console.log('nameIconArr_zz:', nameIconArr_zz)
      console.log('valueNameArr_zz:', valueNameArr_zz)
      
  ///////////////////////////从民生接口响应中获取民生服务////////////////////////////////////////////////////////////////////
      const aiRows_ms = all.filter(r => r?.Belonging?.trim() === '民生服务')

      const byType_ms = new Map()
      for (const r of aiRows_ms) {
        const type_ms = (r?.Level_1_Type ?? '未知类型').trim()
        const status_ms = (r?.Level_2_Type ?? '').trim()
        const val_ms = Number(r?.Numerical_Value ?? 0)

        if (type_ms === '医疗服务' && status_ms === '医师数量') {
          numofdctor.value = val_ms
          console.log("type_ms:", type_ms, "status_ms", status_ms, "numofdctor.value:", numofdctor.value)
          continue
        }
        if (type_ms === '周边商家数') {
          goldenIdeasTotal.value = val_ms
          console.log("type_ms:", type_ms, "status_ms", status_ms, "goldenIdeasTotal.value:", goldenIdeasTotal.value)
          continue
        }
        if (type_ms === '医疗服务' && status_ms === '线上预约数量') {
          numofonlineappts.value = val_ms
          console.log("type_ms:", type_ms, "status_ms", status_ms, "numofonlineappts.value:", numofonlineappts.value)
          continue
        }

        // 只对“就业服务”类型分组和累加
        if (type_ms === '就业服务') {
          if (!byType_ms.has(status_ms)) {
            byType_ms.set(status_ms, { num_ms: 0 })
          }
          const bucket_ms = byType_ms.get(status_ms)
          if (bucket_ms) {
            bucket_ms.num_ms += val_ms
          }
        }
      }
      
      // 语法说明：
      // 步骤详细拆解：
      // 1. byType_xt.keys()：获取Map里所有的key（类型名），结果是一个“迭代器”，不是数组。
      // 2. Array.from(...)：把迭代器变成真正的数组，比如['高空抛物', '垃圾清理', ...]。
      // 3. .map(...)：对数组里的每个类型名都执行一次回调函数。
      // 4. 回调函数 name => ({ name, icon: 'circle' })：把每个类型名变成一个对象，包含name字段和icon字段。
      // 5. 最终得到一个新数组，每个元素都是{name: 类型名, icon: 'circle'}。
      // 业务含义：这个数组可以直接用于图表的图例、标签等地方。
      const nameIconArr_ms = Array.from(byType_ms.keys()).map(name => ({
        name,
        icon: 'circle'
      }))

      const keys_ms = Array.from(byType_ms.keys())
      const valueNameArr_ms = keys_ms.map((name, idx) => ({
        value: byType_ms.get(name).num_ms,
        name,
        itemStyle: { color: `hsl(${(idx * 360) / keys_ms.length}, 70%, 50%)` }
      }))
      console.log('nameIconArr_ms:', nameIconArr_ms)
      console.log('valueNameArr_ms:', valueNameArr_ms)
      // const allTypes_xt = Array.from(byType_xt.keys())
      // const firstValue = byType_xt.get(firstType)                 // 取第一个key对应的值对象
      // const num_xt = firstValue.num_xt 
  ////////////////////////////////从民生接口响应中获取便民服务////////////////////////////////////////////////////////
const aiRows_bm = all.filter(r => r?.Belonging?.trim() === '金点子')

      const byType_bm = new Map()
      for (const r of aiRows_bm) {
        const type_bm = (r?.Level_1_Type ?? '未知类型').trim()
        const status_bm = (r?.Level_2_Type ?? '').trim()
        const val_bm = Number(r?.Numerical_Value ?? 0)

        // if (type_bm === '金点子总数') {
        //   goldenIdeasTotal.value = val_bm
        //   console.log("type_bm:", type_bm, "status_bm", status_bm, "goldenIdeasTotal.value:", goldenIdeasTotal.value)
        //   continue
        // }
        // if (type_bm === '金点子未完成' && status_bm === '线上预约数量') {
        //   goldenIdeasPending.value = val_bm
        //   console.log("type_bm:", type_bm, "status_bm", status_bm, "goldenIdeasPending.value:", goldenIdeasPending.value)
        //   continue
        // }

        // 只对“金点子”类型分组和累加
          if ((!byType_bm.has(type_bm))&& type_bm != '金点子总数'&& type_bm != '金点子未完成') {
            byType_bm.set(type_bm, { num_bm: 0 })
          }
          const bucket_bm = byType_bm.get(type_bm)
          if (bucket_bm) {
            bucket_bm.num_bm += val_bm
          }
        
      }
      
      // 语法说明：
      // 步骤详细拆解：
      // 1. byType_xt.keys()：获取Map里所有的key（类型名），结果是一个“迭代器”，不是数组。
      // 2. Array.from(...)：把迭代器变成真正的数组，比如['高空抛物', '垃圾清理', ...]。
      // 3. .map(...)：对数组里的每个类型名都执行一次回调函数。
      // 4. 回调函数 name => ({ name, icon: 'circle' })：把每个类型名变成一个对象，包含name字段和icon字段。
      // 5. 最终得到一个新数组，每个元素都是{name: 类型名, icon: 'circle'}。
      
      // const allTypes_xt = Array.from(byType_xt.keys())
      // const firstValue = byType_xt.get(firstType)                 // 取第一个key对应的值对象
      // const num_xt = firstValue.num_xt 
  ////////////////////////////////从民生接口响应中获取便民服务////////////////////////////////////////////////////////
const aiRows_zbfw = all.filter(r => r?.Belonging?.trim() === '民生服务')

      const byType_zbfw = new Map()
      for (const r of aiRows_zbfw) {
        const type_zbfw = (r?.Level_1_Type ?? '未知类型').trim()
        const status_zbfw = (r?.Level_2_Type ?? '').trim()
        const val_zbfw = Number(r?.Numerical_Value ?? 0)

        // if (type_zbfw === '金点子总数') {
        //   goldenIdeasTotal.value = val_zbfw
        //   console.log("type_zbfw:", type_zbfw, "status_zbfw", status_zbfw, "goldenIdeasTotal.value:", goldenIdeasTotal.value)
        //   continue
        // }
        // if (type_zbfw === '金点子未完成' && status_zbfw === '线上预约数量') {
        //   goldenIdeasPending.value = val_zbfw
        //   console.log("type_zbfw:", type_zbfw, "status_zbfw", status_zbfw, "goldenIdeasPending.value:", goldenIdeasPending.value)
        //   continue
        // }

        // 只对“金点子”类型分组和累加
          if ((!byType_zbfw.has(type_zbfw))&& type_zbfw != '就业服务'&& type_zbfw != '医疗服务') {
            byType_zbfw.set(type_zbfw, { num_zbfw: 0 })
          }
          const bucket_zbfw = byType_zbfw.get(type_zbfw)
          if (bucket_zbfw) {
            bucket_zbfw.num_zbfw += val_zbfw
          }
        
      }
      
      // 语法说明：
      // 步骤详细拆解：
      // 1. byType_xt.keys()：获取Map里所有的key（类型名），结果是一个“迭代器”，不是数组。
      // 2. Array.from(...)：把迭代器变成真正的数组，比如['高空抛物', '垃圾清理', ...]。
      // 3. .map(...)：对数组里的每个类型名都执行一次回调函数。
      // 4. 回调函数 name => ({ name, icon: 'circle' })：把每个类型名变成一个对象，包含name字段和icon字段。
      // 5. 最终得到一个新数组，每个元素都是{name: 类型名, icon: 'circle'}。
      // 业务含义：这个数组可以直接用于图表的图例、标签等地方。
      // 业务含义：这个数组可以直接用于图表的图例、标签等地方。
      const nameIconArr_zbfw = Array.from(byType_zbfw.keys()).map(name => ({
        name,
        icon: 'circle'
      }))

      const keys_zbfw = Array.from(byType_zbfw.keys())
      const valueNameArr_zbfw = keys_zbfw.map((name, idx) => ({
        value: byType_zbfw.get(name).num_zbfw,
        name,
        itemStyle: { color: `hsl(${(idx * 360) / keys_zbfw.length}, 70%, 50%)` }
      }))
      console.log('nameIconArr_zbfw:', nameIconArr_zbfw)
      console.log('valueNameArr_zbfw:', valueNameArr_zbfw)
      // const allTypes_xt = Array.from(byType_xt.keys())
      // const firstValue = byType_xt.get(firstType)                 // 取第一个key对应的值对象
      // const num_xt = firstValue.num_xt 
  //////////////////////////从民生接口响应中获取基层队伍///////////////////////////////////////////////////////////
  const aiRows_jcdw = all.filter(r => r?.Belonging?.trim() === '基层队伍')

      // const byType_jcdw = new Map()
      for (const r of aiRows_jcdw) {
        const type_jcdw = (r?.Level_1_Type ?? '未知类型').trim()
        const status_jcdw = (r?.Level_2_Type ?? '').trim()
        const val_jcdw = Number(r?.Numerical_Value ?? 0)

        if (type_jcdw === '社区网格数') {
          communityGridCount.value = val_jcdw
          console.log("type_jcdw:", type_jcdw, "status_jcdw", status_jcdw, "communityGridCount.value:", communityGridCount.value)
          continue
        }
        if (type_jcdw === '社区网格员数') {
          communityGridMemberCount.value = val_jcdw
          console.log("type_jcdw:", type_jcdw, "status_jcdw", status_jcdw, "communityGridMemberCount.value:", communityGridMemberCount.value)
          continue
        }
      }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////
      // // 2. 构造数据
      // // eventTypes 获取 y 轴的所有事件类型（如“关爱老人警告”等），是一个字符串数组
      // // 2. 构造数据：直接写死类型列表
      // const eventTypes = [
      //   '电动车进楼栋警告',
      //   '陌生人警告',
      //   '电子围栏警告',
      //   '重点车辆警告',
      //   '重点人员警告'
      // ]
      // // completed 数组，存放每个事件类型“已完成”数量。map 遍历 eventTypes，取出 raw[类型+已完成] 字段并转为数字，若无则为 0
      // const completed = eventTypes.map(type => parseInt(raw[`${type}已完成`] || 0)) 
      // // uncompleted 数组，存放每个事件类型“未完成”数量。map 遍历 eventTypes，取出 raw[类型+未完成] 字段并转为数字，若无则为 0
      // const uncompleted = eventTypes.map(type => parseInt(raw[`${type}未完成`] || 0))

      // 初始化 AI 感知事件数目分析图表，并将实例存入 charts.value.aiEvent
      charts.value.aiEvent = initChart(aiEventChart, {
            ...chartAnimationConfig, // 展开动画配置，统一设置动画效果
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } }, // 提示框配置，鼠标悬停显示详细信息，阴影指示器
            legend: {
              data: ['已完成', '未完成'], // 图例内容，对应 series 的 name
              orient: 'horizontal',     // 图例水平排列
              top: '5%',               // 距离容器顶部 5%
              left: 'center',          // 居中显示
              textStyle: { color: '#fff', fontSize: 10 }, // 图例文字样式
              itemWidth: 10,           // 图例标记宽度
              itemHeight: 8,           // 图例标记高度
              itemGap: 8               // 图例项之间的间距
            },
            grid: { left: '1%', right: '15%', top: '20%', bottom: '15%', containLabel: true }, // 图表网格布局，控制图表绘图区位置
            barCategoryGap: '120%', // 柱状类目间距
            xAxis: { 
              type: 'value',           // x 轴为数值轴
              name: '',                // x 轴名称为空
              max: 120,                // x 轴最大值 120
              axisLabel: { show: false }, // 不显示 x 轴刻度标签
              axisLine: { show: false },  // 不显示 x 轴轴线
              axisTick: { show: false },  // 不显示 x 轴刻度
              splitLine: { show: false }  // 不显示分割线
            },
            yAxis: { 
              type: 'category',         // y 轴为类目轴
              // data: ['电动车进楼栋警告', '陌生人警告', '电子围栏警告', '重点车辆警告', '重点人员警告'], // y 轴类目数据
              data: allTypes, // y 轴类目数据
              axisLabel: { color: '#fff', fontSize: 9 }, // y 轴标签样式
              axisLine: { show: false }, // 不显示 y 轴轴线
              axisTick: { show: false }  // 不显示 y 轴刻度
            },
            series: [
              {
                name: '已完成',         // 系列名称，对应图例
                type: 'bar',           // 柱状图
                stack: 'total',        // 堆叠显示，和“未完成”叠加
                data: completed, // 每个类目的数据，先不填等接口回调
                itemStyle: {
                  color: '#1890ff'     // 柱体颜色
                },
                label: {
                  show: false          // 不显示柱体上的数值标签
                },
                barWidth: '40%'        // 柱体宽度
              },
              {
                name: '未完成',         // 系列名称，对应图例
                type: 'bar',           // 柱状图
                stack: 'total',        // 堆叠显示，和“已完成”叠加
                data: uncompleted, // 每个类目的数据，先不填等接口回调
                itemStyle: {
                  color: '#69c0ff'     // 柱体颜色
                },
                label: {
                  show: false          // 不显示柱体上的数值标签
                },
                barWidth: '40%'        // 柱体宽度
              }
            ]
          })



          // 各类协同治理事件数分析图表
          charts.value.cooperationEvent = initChart(cooperationEventChart, {
            ...chartAnimationConfig,
            tooltip: { 
              trigger: 'item',
              formatter: '{a} <br/>{b}: {c} ({d}%)'
            },
            legend: [
              {
                orient: 'vertical',
                left: '60%',
                top: '13%',
                itemWidth: 12,
                itemHeight: 12,
                itemGap: 10,
                textStyle: {
                  color: '#fff',
                  fontSize: 10
                },
                data: nameIconArr
              }
            ],
            graphic: [
              {
                type: 'image',
                id: 'logo',
                left: '21.5%',
                top: '44%',
                z: -10,
                bounding: 'raw',
                origin: [20, 20],
                position: ['80%', '60%'],
                style: {
                  image: require('@/assets/quan.png'),
                  width: 20,
                  height: 20,
                  opacity: 1
                }
              }
            ],
            series: [
              {
                name: '事件类型',
                type: 'pie',
                radius: ['35%', '65%'],
                center: ['25.5%', '50%'],
                avoidLabelOverlap: false,
                itemStyle: {
                  borderRadius: 0,
                  borderColor: 'transparent',
                  borderWidth: 0
                },
                label: {
                  show: false
                },
                emphasis: {
                  label: {
                    show: true,
                    fontSize: '14',
                    fontWeight: 'bold'
                  }
                },
                labelLine: {
                  show: false
                },
                data: valueNameArr
              }
            ]
          })

          // 各社会组织活动数分析饼图
          charts.value.socialOrg = initChart(socialOrgChart, {
            ...chartAnimationConfig,
            tooltip: {
              trigger: 'item',
              formatter: '{a} <br/>{b}: {c} ({d}%)'
            },
            graphic: [
              {
                type: 'image',
                id: 'logo',
                left: '21%',
                top: '30%',
                z: -10,
                bounding: 'raw',
                style: {
                  image: require('@/assets/quan.png'),
                  width: 20,
                  height: 20,
                  opacity: 1
                }
              }
            ],
            legend: [
              {
                orient: 'vertical',
                left: '60%',
                top: '12%',
                itemWidth: 12,
                itemHeight: 12,
                itemGap: 10,
                textStyle: {
                  color: '#fff',
                  fontSize: 10
                },
                data: nameIconArr_zz
              }
            ],
            series: [
              {
                name: '社会组织',
                type: 'pie',
                radius: ['33%', '60%'],
                center: ['25%', '36%'],
                avoidLabelOverlap: false,
                itemStyle: {
                  borderRadius: 0,
                  borderColor: 'transparent',
                  borderWidth: 0
                },
                label: {
                  show: false
                },
                emphasis: {
                  label: {
                    show: true,
                    fontSize: '14',
                    fontWeight: 'bold'
                  }
                },
                labelLine: {
                  show: false
                },
                data: valueNameArr_zz
              }
            ]
          })

          // 户籍人口与流动人口分析图表 - 卧倒的柱状堆积图
          charts.value.populationFlow = initChart(populationFlowChart, {
            ...chartAnimationConfig,
            tooltip: {
              trigger: 'axis',
              axisPointer: {
                type: 'shadow'
              }
            },
            legend: {
              data: ['户籍人口', '流动人口'],
              orient: 'horizontal',
              bottom: '25%',
              left: 'center',
              textStyle: {
                color: '#fff',
                fontSize: 10
              },
              itemWidth: 10,
              itemHeight: 8,
              itemGap: 8
            },
            grid: {
              left: '5%',
              right: '5%',
              bottom: '20%',
              top: '5%'
            },
            xAxis: {
              type: 'value',
              axisLabel: {
                show: false
              },
              axisLine: {
                show: false
              },
              axisTick: {
                show: false
              },
              splitLine: {
                show: false
              }
            },
            yAxis: {
              type: 'category',
              data: ['人口统计'],
              axisLabel: {
                show: false
              },
              axisLine: {
                show: false
              },
              axisTick: {
                show: false
              }
            },
            series: [
              {
                name: '户籍人口',
                type: 'bar',
                stack: 'total',
                data: [2430],
                itemStyle: {
                  color: '#66cbd4 '
                },
                label: {
                  show: false,
                  position: 'inside',
                  color: '#fff',
                  fontSize: 12,
                  formatter: '{c}'
                },
                barWidth: '15%'
              },
              {
                name: '流动人口',
                type: 'bar',
                stack: 'total',
                data: [860],
                itemStyle: {
                  color: '#0e3642'
                },
                label: {
                  show: false,
                  position: 'inside',
                  color: '#fff',
                  fontSize: 12,
                  formatter: '{c}'
                },
                barWidth: '20%'
              },
              {
                name: '户籍人口百分比',
                type: 'bar',
                stack: 'percent1',
                data: [100],
                itemStyle: {
                  color: 'transparent'
                },
                label: {
                  show: true,
                  position: 'top',
                  color: '#fff',
                  fontSize: 12,
                  formatter: function() {
                    const total = 2430 + 860;
                    const percent1 = ((2430 / total) * 100).toFixed(1);
                    return `${percent1}%`;
                  },
                  distance: 15,
                  align: 'left'
                },
                barWidth: '0%',
                z: 10
              },
              {
                name: '流动人口百分比',
                type: 'bar',
                stack: 'percent2',
                data: [100],
                itemStyle: {
                  color: 'transparent'
                },
                label: {
                  show: true,
                  position: 'top',
                  color: '#fff',
                  fontSize: 12,
                  formatter: function() {
                    const total = 2430 + 860;
                    const percent2 = ((860 / total) * 100).toFixed(1);
                    return `${percent2}%`;
                  },
                  distance: 18,
                  align: 'right',
                  padding: [0, -200, 15, 0]
                },
                barWidth: '0%',
                z: 10
              }
            ]
          })

          // 重点人群分析圆环图
          charts.value.keyPopulation = initChart(keyPopulationChart, {
            ...chartAnimationConfig,
            tooltip: {
              trigger: 'item',
              formatter: '{a} <br/>{b}: {c} ({d}%)'
            },
            legend: {
              orient: 'vertical',
              left: '55%',
              top: '10%',
              itemWidth: 8,
              itemHeight: 8,
              itemGap: 8,
              textStyle: {
                color: '#fff',
                fontSize: 9
              },
              data: nameIconArr_sq
            },
            graphic: [
              {
                type: 'image',
                id: 'logo',
                left: '21%',
                top: '37%',
                z: -10,
                bounding: 'raw',
                style: {
                  image: require('@/assets/quan.png'),
                  width: 20,
                  height: 20,
                  opacity: 1
                }
              }
            ],
            series: [
              {
                name: '重点人群',
                type: 'pie',
                radius: ['25%', '50%'],
                center: ['25%', '45%'],
                avoidLabelOverlap: false,
                itemStyle: {
                  borderRadius: 0,
                  borderColor: 'transparent',
                  borderWidth: 0
                },
                label: {
                  show: false
                },
                emphasis: {
                  label: {
                    show: true,
                    fontSize: '12',
                    fontWeight: 'bold'
                  }
                },
                labelLine: {
                  show: false
                },
                data: valueNameArr_sq
              }
            ]
          })

          

          
           // 各类矛盾调解事件数分析饼图
          charts.value.mediationEventPie = initChart(mediationEventPieChart, {
            ...chartAnimationConfig,
            tooltip: {
              trigger: 'item',
              formatter: '{a} <br/>{b}: {c} ({d}%)'
            },
            graphic: [
              {
                type: 'image',
                id: 'logo',
                left: '21%',
                top: '41%',
                z: -10,
                bounding: 'raw',
                style: {
                  image: require('@/assets/quan.png'),
                  width: 20,
                  height: 20,
                  opacity: 1
                }
              }
            ],
            legend: [
              {
                orient: 'vertical',
                left: '60%',
                top: '12%',
                itemWidth: 12,
                itemHeight: 12,
                itemGap: 10,
                textStyle: {
                  color: '#fff',
                  fontSize: 10
                },
                data: nameIconArr_ms
              }
            ],
            series: [
              {
                name: '民生服务',
                type: 'pie',
                radius: ['40%', '80%'],
                center: ['25%', '50%'],
                avoidLabelOverlap: false,
                itemStyle: {
                  borderRadius: 0,
                  borderColor: 'transparent',
                  borderWidth: 0
                },
                label: {
                  show: false
                },
                emphasis: {
                  label: {
                    show: true,
                    fontSize: '14',
                    fontWeight: 'bold'
                  }
                },
                labelLine: {
                  show: false
                },
                data: valueNameArr_ms
              }
            ]
          })

           // 便民服务金点子类型分析饼图
          charts.value.goldenIdea = initChart(goldenIdeaPieChart, {
            ...chartAnimationConfig,
            tooltip: {
              trigger: 'item',
              formatter: '{a} <br/>{b}: {c} ({d}%)'
            },
            graphic: [
              {
                type: 'image',
                id: 'logo',
                left: '21%',
                top: '41%',
                z: -10,
                bounding: 'raw',
                style: {
                  image: require('@/assets/quan.png'),
                  width: 20,
                  height: 20,
                  opacity: 1
                }
              }
            ],
            legend: [
              {
                orient: 'vertical',
                left: '60%',
                top: '12%',
                itemWidth: 12,
                itemHeight: 12,
                itemGap: 10,
                textStyle: {
                  color: '#fff',
                  fontSize: 10
                },
                data: nameIconArr_zbfw
              }
            ],
            series: [
              {
                name: '金点子上报类型',
                type: 'pie',
                radius: ['25%', '50%'],
                center: ['25%', '50%'],
                avoidLabelOverlap: false,
                itemStyle: {
                  borderRadius: 0,
                  borderColor: 'transparent',
                  borderWidth: 0
                },
                label: {
                  show: false
                },
                emphasis: {
                  label: {
                    show: true,
                    fontSize: '14',
                    fontWeight: 'bold'
                  }
                },
                labelLine: {
                  show: false
                },
                data: valueNameArr_zbfw
              }
            ]
          })
          // // 各类矛盾调解事件数分析图表 - 卧倒的柱状堆积图
          // charts.value.mediation = initChart(mediationChart, {
          //   ...chartAnimationConfig,
          //   tooltip: {
          //     trigger: 'axis',
          //     axisPointer: {
          //       type: 'shadow'
          //     }
          //   },
          //   legend: {
          //     data: ['已解决', '待解决'],
          //     orient: 'horizontal',
          //     top: '5%',
          //     left: 'center',
          //     textStyle: {
          //       color: '#fff',
          //       fontSize: 10
          //     },
          //     itemWidth: 10,
          //     itemHeight: 8,
          //     itemGap: 8
          //   },
          //   grid: {
          //     left: '35%',
          //     right: '5%',
          //     bottom: '15%',
          //     top: '20%'
          //   },
          //   xAxis: {
          //     type: 'value',
          //     axisLabel: {
          //       show: false
          //     },
          //     axisLine: {
          //       show: false
          //     },
          //     axisTick: {
          //       show: false
          //     },
          //     splitLine: {
          //       show: false
          //     }
          //   },
          //   yAxis: {
          //     type: 'category',
          //     data: ['生产经营纠纷', '合同纠纷', '房宅基地纠纷', '邻里纠纷', '婚姻家庭纠纷'],
          //     axisLabel: {
          //       color: '#fff',
          //       fontSize: 9
          //     },
          //     axisLine: {
          //       show: false
          //     },
          //     axisTick: {
          //       show: false
          //     }
          //   },
          //   series: [
          //     {
          //       name: '已解决',
          //       type: 'bar',
          //       stack: 'total',
          //       data: [12, 8, 15, 10, 7, 9, 14, 11],
          //       itemStyle: {
          //         color: '#1890ff'
          //       },
          //       label: {
          //         show: false
          //       },
          //       barWidth: '40%'
          //     },
          //     {
          //       name: '待解决',
          //       type: 'bar',
          //       stack: 'total',
          //       data: [3, 2, 5, 4, 1, 3, 4, 2],
          //       itemStyle: {
          //         color: '#69c0ff'
          //       },
          //       label: {
          //         show: false
          //       },
          //       barWidth: '40%'
          //     }
          //   ]
          // })

          // 各类物业报修事件数分析图表 - 卧倒的柱状堆积图
          charts.value.propertyRepair = initChart(propertyRepairChart, {
            ...chartAnimationConfig,
            tooltip: {
              trigger: 'axis',
              axisPointer: {
                type: 'shadow'
              }
            },
            legend: {
              data: ['已修复', '待修复'],
              orient: 'horizontal',
              top: '5%',
              left: 'center',
              textStyle: {
                color: '#fff',
                fontSize: 10
              },
              itemWidth: 10,
              itemHeight: 8,
              itemGap: 8
            },
            grid: {
              left: '35%',
              right: '5%',
              bottom: '15%',
              top: '20%'
            },
            xAxis: {
              type: 'value',
              axisLabel: {
                show: false
              },
              axisLine: {
                show: false
              },
              axisTick: {
                show: false
              },
              splitLine: {
                show: false
              }
            },
            yAxis: {
              type: 'category',
              data: ['管道疏通', '电梯故障', '公共设施', '其他维修'],
              axisLabel: {
                color: '#fff',
                fontSize: 9
              },
              axisLine: {
                show: false
              },
              axisTick: {
                show: false
              }
            },
            series: [
              {
                name: '已修复',
                type: 'bar',
                stack: 'total',
                data: [18, 12, 15, 8, 22, 11],
                itemStyle: {
                  color: '#1890ff'
                },
                label: {
                  show: false
                },
                barWidth: '40%'
              },
              {
                name: '待修复',
                type: 'bar',
                stack: 'total',
                data: [2, 3, 1, 2, 3, 1],
                itemStyle: {
                  color: '#69c0ff'
                },
                label: {
                  show: false
                },
                barWidth: '40%'
              }
            ]
          }) 

    })

    // 3D视图转换按钮相关
    const only3D = ref(false)
    const toggle3D = () => { only3D.value = !only3D.value }

    // ✅ 拿到 3D 子组件实例，用来调用它暴露的方法
    const viewer3d = ref(null)



    // const handleSelect = (node) => {
    //   // 先占位：点击树节点的回调（后续这里对接 3D 定位）
    //   // 例：DDModelViewer 暴露的定位方法 -> viewerRef.value?.focus(node)
    //   console.log('选中：', node)
    // }

    // 3D 树结构相关
    const exp = reactive({ hardware: true, buildings: true })
    // 数据源（按需替换）
    const hardwareList = ['摄像头1','摄像头2','摄像头3','烟感1','井盖1']
    const groupExpanded = reactive({ 
                          buildings: true, // “建筑”大类默认展开
                          jianzhu: true, 
                          DM: true, 
                          top: false, 
                          other: false 
                         })
    


    // 由子组件（3D 场景）抛上来的“可点击楼栋模型名”
    const modelNames = ref([])

    // 子组件事件回调：接住 emit('buildings-update', names)
    function onBuildingsUpdate(names) {
      modelNames.value = Array.isArray(names) ? names : []
      console.log('接收到可点击楼栋：', modelNames.value)
    }

    // ② 辅助：取显示名、强制分组路径、自动分组
    const labelOf = (raw) => {
      const m = buildingNameMap?.[raw]
      return typeof m === 'string' ? m : m?.label ?? raw
    }
    const forcedPath = (raw) => {
      const m = buildingNameMap?.[raw]
      return (m && typeof m === 'object' && m.path) || null
    }
    const autoGroup = (raw) => {
      if (/^DM/i.test(raw)) return 'DM'
      if (/^top/i.test(raw)) return 'top'
      return 'jianzhu'
    }


    // 目录树二级固定 4 个小区
    const COMMUNITIES = ['保利和园','保利善园','保利臻园','保利佳园A区'];
    // 识别中文前缀到小区名的映射（“模型中文名包含于二级目录名”这一条规则）
    const PREFIX_TO_COMM = [
      { prefix: '和园',  comm: '保利和园' },
      { prefix: '善园',  comm: '保利善园' },
      { prefix: '臻园',  comm: '保利臻园' },
      { prefix: '佳园',  comm: '保利佳园A区' },
    ];

    // 从中文楼牌里判断属于哪个小区（包含判断）
    function communityOf(cnLabel='') {
      const s = String(cnLabel).trim();
      for (const { prefix, comm } of PREFIX_TO_COMM) {
        if (s.includes(prefix)) return comm;
      }
      return null;
    }

    // —— 楼按小区分组（供“建筑”树用）——
    // 复用你子组件输出的 modelNames 和 labelOf(raw)
    const buildingsByCommunity = computed(() => {
      const groups = Object.fromEntries(COMMUNITIES.map(c => [c, []]));
      for (const raw of (modelNames.value || [])) {
        const label = labelOf(raw);       // 已有：映射成中文显示名（fallback 为 raw）
        const comm  = communityOf(label); // 根据“中文包含”判定分到 4 个社区
        if (!comm) continue;
        groups[comm].push({ key: raw, label }); // key 用模型名 raw，点击时走你已有 handleSelect
      }
      // 按楼号排序（能取到数字就按数字，否则中文排序）
      const toNo = s => {
        const m = String(s||'').match(/(\d+)/);
        return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
      };
      Object.values(groups).forEach(arr => {
        arr.sort((a,b) => {
          const na = toNo(a.label), nb = toNo(b.label);
          if (na !== nb) return na - nb;
          return a.label.localeCompare(b.label, 'zh');
        });
      });
      return groups;
    });

    // —— 硬件树：三级=楼，四级=该楼的设备 ——
    // 设备是从 3D 里已经取过并缓存的（devicesByKey），为了不重复造轮子，
    // 只新增一个懒加载容器与开关状态；真正的数据用子组件暴露的 getter（见 B）
    const hwExpanded = reactive({}); // {comm: boolean}
    const hwBuildingExpanded = reactive({}); // {'comm__cnLabel': boolean}
    const hwDevicesCache = reactive(new Map()); // key = comm__cnLabel -> 设备数组

    function hwKey(comm, cnLabel) { return `${comm}__${cnLabel}`; }

    // 展开某楼时，懒加载设备（通过子组件 viewer3d 暴露的 listDevicesByCnLabel）
    async function ensureHwDevices(comm, bItem) {
      const k = hwKey(comm, bItem.label);
      if (hwDevicesCache.has(k)) return;
      const list = await viewer3d.value?.listDevicesByCnLabel?.(bItem.label) || [];
      hwDevicesCache.set(k, list);
    }

    // 新增：设备点击行为
    function openDevicesForBuilding(comm, b) {
      // 保留“聚焦”体验（可删除）
      viewer3d.value?.focusBuildingByName?.(b.key)
      // 直接让 3D 子组件弹出与气泡一致的“设备信息”表
      viewer3d.value?.openDevicesModalForLabel?.(b.label)
    }

    async function openLooseDevices() {
      // 优先走子组件同款弹窗（下面第③步会加）
      if (viewer3d.value?.openLooseDevicesModal) {
        viewer3d.value.openLooseDevicesModal()
        return
      }
      // 兜底：没有暴露 openLooseDevicesModal 时，也能弹表
      const rows = await (viewer3d.value?.listLooseDevicesAll?.() || Promise.resolve([]))
      // 复用 3D 子组件的设备弹窗：给它一个“临时楼名”标题
      // 如果没有公共 openModal，这个兜底可以换成你自己页面的简易弹窗组件
      viewer3d.value?.openDevicesModalForLabel?.('裸设备')  // 先打开壳
      // 由于 openDevicesModalForLabel 内部会立即拉取楼设备，这里简单起见：
      // 推荐按第③步在子组件里提供 openLooseDevicesModal，避免自己改 DOM。
    }

    //////////////////////////

    // —— 小区维度的“裸设备”缓存 & 展开状态
    const hwLooseCache = reactive(new Map());    // key = 社区名 → rows[]
    const hwLooseExpanded = reactive({});        // { '保利善园': true/false, ... }

    // 懒加载：从 3D 子组件取“某小区的裸设备”
    async function ensureHwLoose(comm) {
      if (hwLooseCache.has(comm)) return;
      const rows = await viewer3d.value?.listLooseDevicesByCommunity?.(comm) || [];
      hwLooseCache.set(comm, rows);
    }

    // （可选）点裸设备时的简单详情
    function openLooseDeviceModal(row) {
      console.log('[裸设备]', row);
      // 也可以在这里复用你已有的弹窗渲染
    }


   

    ///////////////////////////

    // ③ 生成分组树（供模板里的 buildingTree 使用）
    const buildingTree = computed(() => {
      const roots = {
        jianzhu: { key: 'jianzhu', label: '建筑', children: [] },
        DM:      { key: 'DM',      label: 'DM',  children: [] },
        top:     { key: 'top',     label: 'Top', children: [] },
      }
      for (const raw of modelNames.value) {
        const path = forcedPath(raw)
        const gkey = path?.[0] ?? autoGroup(raw)
        const group = roots[gkey] || roots.jianzhu
        group.children.push({ key: raw, label: labelOf(raw) })
      }
      Object.values(roots).forEach(g =>
        g.children.sort((a, b) => a.label.localeCompare(b.label, 'zh-Hans-CN'))
      )
      return Object.values(roots).filter(g => g.children.length > 0)
    })

    // ④ 确保 groupExpanded 有对应 key（新分组时默认展开）
    watch(buildingTree, (groups) => {
      groups.forEach(g => {
        if (!(g.key in groupExpanded)) groupExpanded[g.key] = true
      })
    }, { immediate: true })

    // A) 准备三个小栏目（从 buildingTree 中摘出需要的三组）
    //    —— 如果想把显示名改中文，把 label 改成 '建筑' / '电缆沟' / '屋顶' 也行
    // 从 buildingTree 拆成 jianzhu / DM / top 三个数组（按键取，用模板竖着渲染）
    const buildingGroups = computed(() => {
      const out = { jianzhu: [], DM: [], top: [] }
      const groups = buildingTree.value || []
      for (const g of groups) {
        if (g && g.key && Object.prototype.hasOwnProperty.call(out, g.key)) {
          out[g.key] = Array.isArray(g.children) ? g.children : []
        }
      }
      return out
    })


    // 展开/收起某一组
      function toggleGroup(key) {
        groupExpanded[key] = !groupExpanded[key]
      }



    // // ⑤ 列表/树点击 => 让 3D 对焦
    // function handleSelect(target) {
    //   if (target?.type === 'building') {
    //     viewer3d.value?.focusBuildingByName?.(target.name)
    //   } else if (target?.type === 'device') {
    //     console.log('选中硬件：', target.name)
    //   }
    // }

    // （可选）页面挂载后主动拉一次全量清单
    onMounted(async () => {
      await nextTick()
      const names = await viewer3d.value?.listAllBuildings?.()
      if (Array.isArray(names) && names.length) modelNames.value = names
    })

    // 记得把这些返回出去（在你的 return 里追加）
    /*
    return {
      ...,
      onBuildingsUpdate,
      modelNames,
      buildingTree,
      groupExpanded,
    }
    */


    // 选中高亮
    const activeBuilding = ref('')

    // 树节点点击
    function handleSelect(node) {
      if (node.type === 'building') {
        activeBuilding.value = node.name
        // ✅ 通过标签（“1号楼/2号楼”）聚焦
        if (node.type === 'building') {
          viewer3d.value?.focusBuildingByName?.(node.name)   // ✅ 用模型名聚焦
          // 或者如果想用中文显示名：
          // viewer3d.value?.focusBuildingByLabel?.(node.label)
        }


        // 若你想直接用 GLB 名称，换下面这一句：
        // viewer3d.value?.focusBuildingByName('L1-3')
      } else if (node.type === 'device') {
        // 设备的点击逻辑（以后再接）
        console.log('选中设备：', node.name)
      }
    }

    return {
      myNumberAI,               // AI感知事件数
      snapReportValue,         // 随手拍上报数
      snapSolvedValue,         // 随手拍待解决数
      TotalPopulation,         // 总人口
      MalePopulation,         // 男性人口
      FemalePopulation,         // 女性人口
      NumofCommunityPartyMembers, // 社区党员数
      NumofCommunityPartyOrganizations, // 社区党组织数
      NumofsmallCommunityrankDeliberationsvalue, // 小区级议事
      NumofCommunityrankDeliberationsvalue, // 社区级议事
      NumofCommunityDeliberationsvalue, // 议事总数
      numofdctor,               // 医生数
      numofonlineappts,         // 线上预约数
      goldenIdeasTotal,      // 金点子总数
      goldenIdeasPending,    // 金点子待处理数
      communityGridCount,   // 网格总数
      communityGridMemberCount, // 网格员总数
      hd1,                     // 活动内容1
      hd2,                     // 活动内容2
      hd3,                     // 活动内容3
      hd1time,                 // 活动1时间
      hd2time,                 // 活动2时间
      hd3time,                 // 活动3时间
      open,                     //数字人开关
      charts,
      isFullscreen,
      handleFullscreenChange,
      aiEventChart,
      cooperationEventChart,
      socialOrgChart,
      populationFlowChart,
      keyPopulationChart,
      mediationChart,
      mediationEventPieChart,
      goldenIdeaPieChart,
      propertyRepairChart,
      toggle3D,

      // 3D 组件
      viewer3d,

      // 树状卡片，面板 & 数据
      only3D, exp, hardwareList,  activeBuilding,

      // 交互
      handleSelect,
      onBuildingsUpdate,
      modelNames,
      buildingTree,
      groupExpanded,
      buildingGroups,
      toggleGroup,


      COMMUNITIES,
      buildingsByCommunity,
      hwExpanded,
      hwBuildingExpanded,
      hwDevicesCache,
      ensureHwDevices,
      communityOf,
      //设备裸列表
      hwLooseCache, hwLooseExpanded, ensureHwLoose, openLooseDeviceModal,
      openDevicesForBuilding,openLooseDevices,
      scaleStyle,
      screenRef
    }
  }
}
</script>

<style scoped>
.wisdom-community-screen {
  background-color: #000 !important;
  color: #fff;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  margin: 0;
  padding: 0;
}

.screen-adapter {
  transform-origin: 0 0;
  position: absolute;
  left: 0;
  top: 0;
  transition: transform 0.3s ease;
  background: #000;
  overflow: hidden;
}


.main-content {
  position: relative;       /* 作为定位容器（关键） */
  width: 100%;
  /*height: 100vh;  /* ← 根据你实际头部高度调整；不知道就先用 100vh */
  /*min-height: 100vh;    /* 不再固定高度 */
  /*height: auto;*/
  /* height: 100%;  ← 删掉这句！它会覆盖上面的 calc */
  overflow: hidden;
  background: transparent;  /* 避免自己黑底 */
  /*padding-top: 30px; /* 内边距，避免内容贴边 */
  height: 100%;          /* 让 absolute 子元素用 top/bottom 定高 */
  min-height: 0;
  overflow: hidden;      /* 不把滚动交给这里 */
  background: transparent;
  margin: 0;
  padding: 0;
}
/*
  【专业词解释】
  - 弹性盒布局（flex）：一种 CSS 布局方式，能让一行或一列里的多个元素自动分配空间、对齐和换行。
  - 子元素：某个 HTML 元素内部嵌套的元素。
  - 间距（gap）：设置子元素之间的空隙距离。
  - 视口高度（vh）：1vh 等于浏览器窗口高度的 1%。
  - calc()：CSS 的计算函数，可动态计算长度。
  - 横向排列：元素在一行里从左到右依次排列。
  - 自动分配空间：flex 布局下，元素会根据剩余空间自动调整宽度或高度。
*/

.map-bg {
  width: 100%;
  z-index: 0;         /* 放底层 */
  border: 0;
  display: block;
  position: absolute;
  inset: 0;
  height: 100%;
}



/* 必须 absolute + inset:0 才能铺满父容器 */
.content-layer {
  position: absolute;
  inset: 0;     /* top:0; right:0; bottom:0; left:0 */
  z-index: 1;   /* 盖在 3D 背景（z-index:0）之上 */
}
/* 只看3D模式：允许 3D 交互（旋转/缩放/拖拽） */
.main-content.only-3d .map-bg {
  pointer-events: auto;
}

/* 按钮（毛玻璃+半透明，尽量贴合数据大屏的主题风格） */
.toggle-3d-btn {
  position: absolute;
  top: 12px;
  /* right: 16px; */
  z-index: 10;
  left: 50%;
  transform: translateX(-50%);  /* 关键：以自身宽度的 50% 向左偏移 */
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;

  /* 主题友好：暗色毛玻璃 */
  color: var(--theme-text, #e5e7eb);
  background: rgba(15, 23, 42, 0.55);           /* slate-900 55% */
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(6px);
}
.toggle-3d-btn:hover {
  border-color: rgba(99, 102, 241, 0.9);        /* 近似 primary */
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.25);
}
.toggle-3d-btn:active { transform: translateY(1px); }

/* 只看3D时显示的树状卡片 */
.tree-panel{
  position: absolute;
  top: 56px;          /* 在按钮下方 */
  right: 16px;        /* 你也可以改成 left:16px */
  z-index: 20;
  width: 260px;
  max-height: 70vh;
  overflow: auto;
  padding: 12px;
  border-radius: 12px;
  background: rgba(15,23,42,.60);
  border: 1px solid rgba(255,255,255,.12);
  box-shadow: 0 8px 24px rgba(0,0,0,.35);
  backdrop-filter: blur(6px);
}
.tree-panel__title{
  color: #e5e7eb; font-size: 14px; font-weight: 600;
  letter-spacing: .5px; margin-bottom: 8px;
}
.tree{ list-style:none; margin:0; padding:0; }
.tree > li{ margin:4px 0; }

.tree-toggle{
  width: 1.25em; height: 1.25em;
  background: transparent; border: none; cursor: pointer;
  color: #a5b4fc; font-size: 14px; line-height: 1;
  vertical-align: middle;
}
.tree-label{ color:#d1d5db; font-weight:600; margin-left:4px; }

.tree ul{
  list-style: none; margin:6px 0 6px 1.1em; padding:0;
  border-left: 1px dashed rgba(148,163,184,.35);
}
.tree-leaf{
  color:#e5e7eb; padding:4px 6px; margin:2px 0;
  border-radius: 6px; cursor: pointer;
}
.tree-leaf:hover{ background: rgba(99,102,241,.15); }


/* 父页 scoped 样式里加 */
.tree-leaf { cursor: pointer; padding: 4px 8px; border-radius: 6px; }
.tree-leaf:hover { background: rgba(0,0,0,.06); }
.tree-leaf.active { background: rgba(59,130,246,.12); color: #2563eb; }
.tree-toggle { border:0; background:transparent; cursor:pointer; margin-right:6px; }
.tree-label { font-weight: 600; }




/* 主内容区三栏布局 */
.left-section, .right-section {
  margin-top: 8px;
  position: absolute;       /* 浮在地图之上（关键） */
  top: 10px;
  bottom: 10px;
  width: 30%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  /*padding-top: 50px;  /* 顶部内边距10像素，避免内容贴边 */
  z-index: 2;               /* 盖过 iframe */
  /* 你说要整体透明： */
  opacity: 0.85;            /* 整块一起变透明；不要再设 background */
  background: transparent;
  color: #fff;
  /* padding: 10px; */
  border-radius: 8px;
  /* overflow-y: auto;滚动条去除 */
  pointer-events: auto;     /* 面板可点击 */
  -webkit-overflow-scrolling: touch; /* 更顺滑 */
  padding-right: 6px;   /* 给滚动条留点空间，防止盖住内容 */
}

.left-section {
  width: 35%;              /* 左区块实际宽度为主内容区的35%（覆盖上面的30%） */
  /*margin-left: -20px;      /* 左侧整体向左偏移20像素，拉近与边界的距离 */
  left: 20px;              /* 左侧距离20像素，避免紧贴边界 */
}

.right-section {
  width: 35%;              /* 右区块实际宽度为主内容区的35%（覆盖上面的30%） */
  /*margin-right: -20px;     /* 右侧整体向右偏移20像素，拉近与边界的距离 */
  right: 20px;             /* 右侧距离20像素，避免紧贴边界 */
  overflow-y: auto;
}

.left-section .bordered-section:nth-child(2) {
  flex: 2.0;               /* 基层自治块内容较多，分配更多空间 */
  margin-top: 0 !important; /* 跟随上面的 gap，不再另加外边距 */
  overflow: visible;       /* 内容溢出时不裁剪，全部显示出来 */
  position: relative;      /* 相对定位，便于后代元素绝对定位 */
}

/* 确保左侧第一个块也能伸展 */
.left-section .charts-container {
  flex: 1.2;
}

.middle-section {
  width: 40%;              /* 中间区块占主内容区的40%宽度 */
  display: flex;           /* 启用弹性盒布局，让子内容纵向排列 */
  flex-direction: column;  /* 子元素垂直排列（从上到下） */
  gap: 8px;                /* 子元素之间的间距为8像素 */
  margin-left: 0.8px;      /* 向左微调0.8像素，视觉对齐用 */
  margin-top: -45px;       /* 顶部向上偏移45像素，让内容更紧凑 */
  flex: 1;                 /* 使用 flex: 1 替代固定高度，自适应容器 */
}

.bordered-section {
  border-radius: 4px;       /* 边角圆润，半径4像素 */
  padding: 8px;            /* 内边距8像素，内容不贴边 */
  background: linear-gradient(135deg, #122a44, #0a1a2a); /* 渐变背景色，美化视觉效果 */
  position: relative;      /* 相对定位，便于后代元素绝对定位 */
  /* 相对定位：让元素在原位基础上微调，也给子元素绝对定位提供参照点 */
  overflow: hidden;        /* 内容溢出时裁剪，防止溢出边界 */
  /* 溢出：内容超出盒子时会被裁剪，看不到 */
  border: none;            /* 去除默认边框 */
  /* 边框：去掉元素自带的线条包围 */
  display: flex;
  flex-direction: column;
}

.bordered-section::before {
  display: none;            /* 伪元素不显示，防止出现多余装饰 */
}

/* 移除边框的样式 */
.no-border {
  border: none !important;
  box-shadow: none !important;
}

.no-border::before {
  display: none !important;
}

/* 带中间分割线的flex布局 */
.with-divider {
  position: relative;
}

/* 中间蓝色分割线 */
.with-divider::after {
  display: none;
}

/* 移除子容器边框的样式 */
.no-sub-border {
  border: none !important;
  box-shadow: none !important;
}

/* 移除容器背景色的样式 */
.no-background {
  background-color: transparent !important;
}

/* 标题框样式 */
.title-box {
  border-radius: 4px;
  background: #000000;
  padding: 8px;
  margin: 4px 0;
  border: none;
}

.title-box-header {
  color: #fff;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 6px;
  padding-bottom: 4px;
}

.title-box-content {
  color: white;
  font-size: 12px;
  min-height: 20px;
}

.section-title,
.chart-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 12px;
  color: white;
  display: flex;
  align-items: center;
}

/* 基层队伍跳转 */
/* 保持原本样式 */
.section-title .label-link {
  font-family: 'Source Han Serif CN', 'Noto Serif CJK SC', '思源宋体', serif;
  letter-spacing: 2px;
  vertical-align: middle;     /* 和两侧图片统一对齐 */
  text-decoration: none;      /* 去掉下划线 */
  color: inherit;             /* 继承原色 */
  cursor: pointer;            /* 用于将鼠标指针样式设置为手形，通常表示该元素是可点击的交互元素.鼠标变手指 */
}

/* 访问过也不变色、不下划线 */
.section-title .label-link:visited {
  color: inherit;
  text-decoration: none;
}

/* 悬停/键盘聚焦：文字变蓝、无下划线 */
.section-title .label-link:hover,
.section-title .label-link:focus {
  color: #1677ff;             /* 你也可以用 #1890ff 等蓝色 */
  text-decoration: none;
  outline: none;                 /* 样式规则，用于取消元素获得焦点时的默认轮廓线。去掉虚线框 */
}


.chart-title {
  color: #fff;
  justify-content: center;
  padding: 3px 20px;
  border-bottom: none;
  margin-bottom: 1px;
  margin-top: 5px;
  font-size: 12px;
  font-weight: 300;
  background-image: url('@/assets/diwen.png');
  background-size: auto 80%;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  align-items: center;
}

.population-flow-title {
  margin-top: 15px;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 12px;
  color: white;
  display: flex;
  align-items: center;
}

.slash {
  font-weight: bold;
  margin-left: 2px;
}

.slash1 {
  color: #1890ff; /* 浅蓝色 */
}

.slash2 {
  color: #0066cc; /* 中等蓝色 */
}

.slash3 {
  color: #003d7a; /* 深蓝色 */
}

.community-info {
  background: #000000;
  padding: 12px;
  border-radius: 4px;
  height: 120px;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.5;
  border: none;
}

.chart-container {
  flex: 1;
  width: 100%;
  min-height: 0;
  border: none;
  outline: none;
}

.chart-container > div:last-child {
  flex: 1;
  width: 100%;
  border: none;
  outline: none;
  box-shadow: none;
}


.map-container {
  flex: 1;                  /* 在弹性布局中，自动占据剩余空间 */
  width: 100%;              /* 宽度占满父容器 */
  height: 100%;             /* 高度占满父容器 */
  min-height: 200px;        /* 最小高度200像素，防止太小 */
  display: flex;            /* 启用弹性盒布局，让内容居中 */
  justify-content: center;  /* 水平居中内容 */
  align-items: center;      /* 垂直居中内容 */
  border-radius: 4px;       /* 边角圆润，半径4像素 */
  overflow: hidden;         /* 内容溢出时裁剪，防止溢出边界 */
}

.map-container img {
  border-radius: 4px;       /* 图片边角圆润，和容器一致 */
  box-shadow: none;         /* 去掉图片阴影效果 */
}

.concern-item {
  background: #000000;      /* 背景色为黑色 */
  padding: 12px;            /* 内边距12像素，内容不贴边 */
  border-radius: 4px;       /* 边角圆润，半径4像素 */
  flex: 1;                  /* 在弹性布局中，自动占据剩余空间 */
  display: flex;            /* 启用弹性盒布局 */
  flex-direction: column;   /* 子元素垂直排列（从上到下） */
}

.concern-item > div:last-child {
  flex: 1;                  /* 自动占据剩余空间，撑满父容器 */
  width: 100%;              /* 宽度占满父容器 */
}

.concern-title {
  font-size: 14px;          /* 字体大小14像素 */
  margin-bottom: 10px;      /* 下方外边距10像素，和内容拉开距离 */
  color: #ffffff;           /* 字体颜色为白色 */
}

/* 分割容器样式 */
.split-container {
  display: flex;
  width: 100%;
  height: 350px;
  align-items: stretch;
  margin-top: 5px;
}

/* 男女人口比例图表样式 - 占容器三分之一 */
.left-content .gender-ratio-chart:first-child {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-radius: 8px;
  padding: 10px;
}

/* 户籍人口与流动人口分析图表样式 - 占容器三分之二 */
.left-content .gender-ratio-chart:last-child {
  flex: 2;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-radius: 8px;
  padding: 10px;
}

/* 图表容器样式 */
.gender-ratio-chart .chart-container,
.population-flow-chart .chart-container {
  flex: 1;
  min-height: 0;
  width: 100%;
}

/* 男女人口比例图表中的条形图向下移动 */
.gender-ratio-chart .chart {
  margin-top: 25px;
  height: 200px;
}

/* 户籍人口与流动人口分析折线图高度 */
.population-flow-chart-height {
  margin-top: 10px;
  height: 200px;
  padding: 10px;
}



/* 右侧区域容器大小控制 */
.right-section .bordered-section:nth-child(1) {
  flex: 0.8;
}

.right-section .service-container:nth-child(2) {
  flex: 1.4; /* 平衡三个块的比例 */
}

.right-section .bordered-section:nth-child(3) {
  flex: 0.7;
}

/* 右侧内容区域样式 */
.right-content {
  flex: 1;
  min-height: 120px;
  position: relative;
  display: flex;
  flex-direction: column;
}

.left-content,
.right-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-radius: 4px;
  width: 50%;
  min-width: 0;
  box-sizing: border-box;
}

.vertical-divider {
  display: none;
}

.event-list {
  background-color: rgba(255, 255, 255, 0.05);
  padding: 12px;
  border-radius: 4px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.event-item {
  background: #000000;
  padding: 12px;
  border-radius: 6px;
  border-left: 3px solid #1890ff;
  transition: all 0.3s;
  transform-origin: left;
}

:deep(.event-item:hover) {
  transform: translateX(5px);
  background-color: rgba(255, 255, 255, 0.05);
}

.event-title {
  font-weight: bold;
  margin-bottom: 5px;
  font-size: 14px;
}

.event-progress {
  margin-bottom: 5px;
}

.event-time {
  font-size: 12px;
  color: white;
}

.activity-list {
  background-color: rgba(255, 255, 255, 0.05);
  padding: 8px;
  border-radius: 4px;
  height: 60px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.activity-item {
  background: #000000;
  padding: 12px;
  border-radius: 6px;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
}

:deep(.activity-item:hover) {
  background-color: rgba(255, 255, 255, 0.05);
}

.activity-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 12px 12px 0 0;
  border-color: white transparent transparent transparent;
}

.activity-date {
  font-weight: bold;
  color: #1890ff;
  margin-bottom: 5px;
  font-size: 14px;
}

.activity-date {
  font-weight: bold;
  color: #1890ff;
  margin-bottom: 5px;
  font-size: 14px;
}

.activity-title {
  margin-bottom: 5px;
  font-size: 14px;
}

.activity-location {
  font-size: 12px;
  color: white;
}

/* 新增：左右容器的 flex 布局 */
.chart-row {
  display: flex;
  gap: 16px; /* 左右容器之间的间距 */
  position: relative;
}

/* 左右容器之间的蓝色分隔线 */
.chart-row::after {
  display: none;
}

/* 左右容器通用样式 */
.chart-container {
  flex: 1; /* 让两个容器平分父级宽度 */
  height: 100%; /* 使用 100% 高度以填满父容器 */
  display: flex;
  flex-direction: column;
  min-height: 120px;
  overflow: visible;
}

/* 男女人口比例圆环图容器 */
.gender-ratio-container {
  height: 120px !important;
  min-height: 120px !important;
  flex: 0 1 auto;
  padding: 5px 0;
  margin-top: 0;
}

/* 图表元素样式 */
.chart {
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0; /* 防止flex子元素溢出 */
}

/* 社区事件饼图样式 - 缩小饼图 */
.cooperation-chart {
  height: 70% !important;
  margin: 0 auto;
}

/* 左侧容器单独样式（与父容器边框样式一致） */
.left-chart {
  border-radius: 4px;
  background: linear-gradient(135deg, #041e33 0%, #0a2847 40%, #122a44 60%, #1a3a5c 100%);
  padding: 8px;
  padding-bottom: 20px;
  box-sizing: border-box;
  flex: 1;
  /* margin-right: 8px; */
  box-shadow: inset 0 0 30px rgba(0, 0, 0, 0.7), 0 0 15px rgba(25, 118, 210, 0.2);
  position: relative;
  overflow: hidden;
  height: 100%;
}

.left-chart::before,
.right-chart::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0) 70%);
  pointer-events: none;
}

/* 子容器容器样式 */
.sub-containers {
  display: flex;
  gap: 8px;
  margin-bottom: 5px;
  margin-top: 15px;
  height: 35px;
}

/* 单个子容器样式 */
.sub-container {
  flex: 1; /* 在父级为flex布局时，当前子容器会按1.5的比例分配剩余空间。比如有多个子容器，flex值越大占空间越多。这里1.5表示比flex:1的容器更宽。 */
}

/* 服务容器样式 */
.service-container {
  display: flex;
  gap: 8px;
  position: relative;
  min-height: 0;
}

.service-left, .service-right {
  flex: 1;
  background: linear-gradient(135deg, #122a44, #0a1a2a);
  border-radius: 4px;
  padding: 8px;
  position: relative;
  overflow: hidden;
  border: none;
  display: flex;
  flex-direction: column;
}

/* 单个服务容器样式 */
.service-container.single-service {
  height: 120px;
  gap: 0;
}

/* 全宽服务容器样式 */
.service-full {
  flex: 1;
  border-radius: 6px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  background: #000000;
  width: 100%;
}

/* 服务容器带分隔线的样式 */
.service-container.with-divider::after {
  display: none;
}

/* 服务左右容器通用样式 */
.service-left,
.service-right {
  flex: 1;
  border-radius: 6px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #122a44, #0a1a2a);
}

/* 扩大矛盾调解服务容器 */
.service-left {
  flex: 1;
  /* margin-left: -10px; */
}

/* 扩大便民服务容器 */
.service-right {
  /* margin-right: -10px; */
  flex: 1;
}

/* 服务标题样式 */
.service-title {
  font-size: 12px;
  font-weight: bold;
  color: #fff;
  text-align: center;
  margin-bottom: 3px;
  padding-bottom: 2px;
  border-bottom: none;
}

/* 服务子标题样式 */
.service-subtitle {
  font-size: 12px;
  font-weight: bold;
  color: white;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: none;
  background: linear-gradient(to right, rgba(60, 107, 142, 0.2), rgba(60, 107, 142, 0.5));
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
}

/* 服务总数标题样式 */
.service-total-title {
  font-size: 12px;
  font-weight: bold;
  color: white;
  margin-bottom: 15px;
  margin-top: -15px;
  padding: 6px 8px;
  border-radius: 3px;
  display: inline-block;
  background: linear-gradient(to left, rgba(14, 54, 66, 0.8), rgba(14, 54, 66, 0.2));
  height: 30px;
  line-height: 18px;
}

/* 服务计数样式 */
.service-count {
  font-size: 14px;
  font-weight: bold;
  color: #40a9ff;
  margin-left: 70px;
  text-shadow: 0 0 5px rgba(64, 169, 255, 0.5);
}

/* 待处理计数样式 - 位于总数正下方 */
.pending-count {
  margin-left: 73px;
}

/* 右侧待处理计数样式 - 与左侧待处理计数对齐 */
.pending-right-count {
  margin-left: 50px;
}

/* 总数计数样式 - 位于待处理数正上方 */
.total-count {
  margin-left: 75px;
}

/* 服务单位样式 */
.service-unit {
  font-size: 14px;
  font-weight: bold;
  color: white;
  margin-left: 4px;
}

/* 服务待处理标题样式 */
.service-pending-title {
  font-size: 12px;
  font-weight: bold;
  color: white;
  margin-bottom: 15px;
  margin-top: -10px;
  padding: 4px 8px;
  border-radius: 3px;
  display: inline-block;
  background: linear-gradient(to left, rgba(150, 19, 19, 0.6), rgba(15, 66, 83, 0.6));
  height: 24px;
  line-height: 16px;
}

/* 服务内容容器样式 */
.service-content {
  display: flex;
  flex: 1;
  position: relative;
  margin-top: 5px;
}

/* 服务内容容器带分隔线的样式 */
.service-content.with-divider::after {
  display: none;
}

/* 服务左右两半部分样式 */
.service-half-left,
.service-half-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  border-radius: 3px;
  color: #fff;
  font-size: 10px;
  text-align: center;
  padding: 4px;
}

/* 新增子容器数据样式 */
.data-row {
  display: flex;
  gap: 16px;
  justify-content: space-around;
  padding: 8px 0;
}

.data-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.data-label {
  font-size: 10px;
  color: white;
  margin-bottom: 4px;
}

.data-value {
  font-size: 14px;
  font-weight: bold;
  color: white;
  text-shadow: 0 0 5px rgba(24, 144, 255, 0.5);
}

/* 服务半部分标题样式 */
.service-half-title {
  font-size: 11px;
  font-weight: bold;
  color: white;
  margin-bottom: 4px;
  text-shadow: 0 0 3px rgba(24, 144, 255, 0.3);
}

/* 服务半部分数值样式 */
.service-half-value {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  text-shadow: 0 0 5px rgba(255, 255, 255, 0.3);
  background: linear-gradient(45deg, #1890ff, #40a9ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  padding: 2px 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.service-half-value:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(24, 144, 255, 0.4);
}

/* 子容器样式 */
.sub-container {
  border-radius: 4px;
  padding: 6px;
  color: #fff;
  font-size: 11px;
  outline: none;
  cursor: text;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #000000;
}

/* 内部子容器样式 */
.inner-sub-container {
  height: calc(50% - 6px); /* 减少高度 */
  border-radius: 3px;
  padding: 4px;
  color: #fff;
  font-size: 9px;
  outline: none;
  cursor: text;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
  overflow: hidden;
  background: #000000;
}

.inner-sub-container:hover {
  color: white;
}

.inner-sub-container:focus {
  color: #fff;
}

/* 分隔线样式 */
.divider {
  display: none;
}

.sub-container:hover {
  color: white;
}

.sub-container:focus {
  color: #fff;
}

/* 左右侧容器通用样式 */
.left-chart,
.right-chart {
  border-radius: 4px;
  background: #122a44;
  padding: 8px;
  padding-bottom: 20px;
  box-sizing: border-box;
  flex: 1;
  height: 300px;
  display: flex;
  flex-direction: column;
}

/* 图表容器布局 */
.charts-container {
  display: flex;                  /* 使用flex布局，让子元素（水平方向）自动排列 */
  gap: 8px;                      /* 子元素之间间距为8px，视觉更美观 */
  margin: 0 !important;           /* 强制去掉所有外边距，防止和其他布局冲突 */
  flex: 1 !important;             /* 参与 flex 伸展，占据剩余高度 */
}

/* 白色竖线样式 */
.white-vertical-line {
  display: inline-block;
  width: 5px;
  height: 16px;
  background-color: white;
  margin-right: 8px;
  vertical-align: middle;
}

/* AI感知事件标题框样式 */
.sub-title {
      background: linear-gradient(to right, rgba(60, 107, 142, 0.2), rgba(60, 107, 142, 0.5));
      padding: 4px 8px;
      border-radius: 4px;
      display: inline-block;
    }
/* AI感知事件标题链接样式 */
a.sub-title {
  text-decoration: none;
  color: inherit;
}
a.sub-title:hover {
  color: #4fc3f7; /* 可选：悬停高亮 */
}

/* 协同治理统计行样式 */
.cooperation-stats-row {
  display: flex;
  justify-content: space-between;
  margin: -20px 8px 0;
  gap: 0;
}

/* 协同治理统计容器样式 */
.cooperation-stats-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 0;
  border-radius: 4px;
  flex: 1;
}

/* 协同治理统计文字样式 */
.cooperation-stats-text {
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
}

/* 协同治理统计数值样式 */
.cooperation-stats-value {
  color: #4fc3f7;
  font-size: 18px;
  font-weight: bold;
}

/* 第一个协同治理统计容器的文字样式 - 网格上报事件数 */
.cooperation-stats-container:first-child .cooperation-stats-text {
  background-color: rgba(60, 107, 142, 0.2);
  padding: 4px 8px;
  border-radius: 0;
  display: inline-block;
  width: 100%;
  text-align: center;
  margin-bottom: 0;
  padding-bottom: 0;
}

/* 第一个协同治理统计容器的数值样式 - 86个 */
.cooperation-stats-container:first-child .cooperation-stats-value {
  background-color: rgba(60, 107, 142, 0.2);
  padding: 4px 8px;
  border-radius: 0;
  display: inline-block;
  width: 100%;
  text-align: center;
  margin-top: 0;
  padding-top: 0;
  color: #4fc3f7;
}

/* 第一个协同治理统计容器的数值中的"个"字样式 */
.cooperation-stats-container:first-child .cooperation-stats-value::after {
  content: "个";
  color: #ffffff;
  margin-left: 2px;
}

/* 第二个协同治理统计容器的文字样式 - 待解决事件数 */
.cooperation-stats-container:last-child .cooperation-stats-text {
  background-color: rgba(60, 107, 142, 1.2);
  padding: 4px 8px;
  border-radius: 0;
  display: inline-block;
  width: 100%;
  text-align: center;
  margin-bottom: 0;
  padding-bottom: 0;
}

/* 第二个协同治理统计容器的数值样式 - 24个 */
.cooperation-stats-container:last-child .cooperation-stats-value {
  background-color: rgba(60, 107, 142, 1.2);
  padding: 4px 8px;
  border-radius: 0;
  display: inline-block;
  width: 100%;
  text-align: center;
  margin-top: 0;
  padding-top: 0;
  color: #4fc3f7;
}

/* 第二个协同治理统计容器的数值中的"个"字样式 */
.cooperation-stats-container:last-child .cooperation-stats-value::after {
  content: "个";
  color: #ffffff;
  margin-left: 2px;
}

/* 事件统计文字样式 */
.event-stats-text {
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  margin-right: 30px;
}

/* 事件统计文字链接样式 */
a.event-stats-text {
  color: #ffffff;         /* 继承你原本的白色 */
  font-size: 12px;
  font-weight: 500;
  margin-right: 30px;
  text-decoration: none;  /* 去掉下划线 */
}
a.event-stats-text:hover {
  color: #4fc3f7;         /* 鼠标悬停时可选高亮色 */
  text-decoration: underline; /* 或者加下划线，看你需求 */
}

/* 事件统计数值样式 */
.event-stats-value {
      color: #4fc3f7;
      font-size: 18px;
      font-weight: bold;
    }
    
    /* 事件统计数值中的"个"字样式 */
    .event-stats-value::after {
      content: "个";
      color: #ffffff;
      margin-left: 2px;
    }

    /* 事件统计容器样式 - 水平布局 */
    .event-stats-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      margin: -10px 0 10px;
    }

    /* 事件统计logo样式 */
    .event-stats-logo {
      width: 30px;
      height: 30px;
      margin-right: 8px;
      vertical-align: middle;
    }

/* 新的弹性布局样式 */
.flex-layout {
  display: flex;
  gap: 12px;
  height: 100%;
}

/* 基层自治专用布局样式 */
.grassroots-layout {
  flex: 1;
  overflow: visible;
  position: relative;
  display: flex;
}

/* 移除全屏模式下的固定高度覆盖，让其跟随父容器 flex 布局 */
/*.fullscreen .grassroots-layout {
  position: fixed;
  top: 10px;
  left: 20px;
  width: 35%;
  height: 550px;
  z-index: 1000;
}*/

.left-column {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.right-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0px;
}

.full-height {
  height: 100%;
}

/* 并排容器样式 */
.side-by-side-containers {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  position: relative;
}

/* 两个子容器之间的蓝色分隔线 */
.side-by-side-containers::after {
  display: none;
}

.side-by-side-containers .sub-container {
  flex: 1;
  border-radius: 4px;
  padding: 8px;
  color: #fff;
  text-align: center;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s;
  background: #000000;
}

.sub-title {
  font-size: 12px;
  font-weight: bold;
  color: white;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: none;
  background: linear-gradient(to right, rgba(60, 107, 142, 0.2), rgba(60, 107, 142, 0.5));
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
}

/* 子标题容器样式 - 用于平分宽度 */
.sub-titles-container {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
  
  /* 等高关键：让子项拉伸并让自身有高度 */
  align-items: stretch;
  flex: 1;
  min-height: 0;  /* 处理嵌套 flex 时的溢出问题 */
}

/* 两列都等分宽度并随父容器等高 */
.sub-titles-container > .vertical-box,
.sub-titles-container > .sub-title {
  flex: 1 1 0;   /* 50/50 */
  min-width: 0;  /* 防止长内容把列撑爆 */
  height: 100%;  /* 跟随父容器高度 */
  box-sizing: border-box;
  overflow: auto; /* 内容超出时内部滚动（可选） */
}

.vertical-box {
  display: flex;
  flex-direction: column; /* 垂直排列 */
  gap: 6px;               /* 子项之间间距 */
  flex: 1;                /* 伸展以填充父容器 */
}

/* 右侧卡片本身样式；去掉 margin-bottom 以保持等高 */
.sub-titles-container .sub-title {
  /* flex: 1;  ← 已在上面统一设置 */
  text-align: left;
  margin-bottom: 0;     /* ← 取消外边距，避免高度不一致 */
  padding-bottom: 10px;
  border-bottom: none;
}

/* 性别数据容器样式 - 包含所有性别图标和数字 */
.gender-data-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5px;
  border-radius: 6px;
  margin-top: -20px;
  width: 45%;
  margin: 0 auto; /* 水平居中 */
}

/* 重点人群分析圆环图样式 */
.key-population-chart {
  width: 100%;
  height: 150px;
  position: relative;
  margin-top: 10px;
  z-index: 10;
}
/* 就业服务组织圆环图样式 */
.mediation-event-pie-chart {
  width: 100%;
  height: 100px;
  /* position: absolute;  // 去掉这一行 */
  /* right: -15px;        // 去掉这一行 */
  /* top: 110px;          // 去掉这一行 */
  z-index: 10;
  /* 可选：加 margin 控制间距 */
  margin: 0 auto;
}
/* 黄金点子圆环图样式 */
.golden-idea-pie-chart {
  width: 100%;
  height: 100px;
  /* position: absolute;  // 去掉这一行 */
  /* right: -15px;        // 去掉这一行 */
  /* top: 110px;          // 去掉这一行 */
  z-index: 10;
  /* 可选：加 margin 控制间距 */
  margin: 0 auto;
}

/* 户籍人口与流动人口分析标题样式 */
.population-analysis-title {
  font-size: 12px;
  font-weight: bold;
  color: white;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: none;
  width: 85%;
  margin-left: 5px;
  background: linear-gradient(to right, rgba(60, 107, 142, 0.2), rgba(60, 107, 142, 0.5));
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
}

/* 户籍人口与流动人口分析图表容器样式 */
.population-flow-chart-container {
  width: 50%;
  height: 100px;
  margin-top: -22px;
  border-radius: 4px;
  padding: 10px;
  box-sizing: border-box;
}

/* 户籍人口与流动人口分析图表样式 */
.population-flow-chart {
  width: 100%;
  height: 100%;
}

/* 性别图标容器样式 */
.gender-icons-container {
  display: flex;
  justify-content: center;
  width: 100%;
  gap: 5px;
  margin-top: 0;
  margin-bottom: 0;
  margin-left: 0;
}

/* 性别图标样式 */
.gender-icon {
  height: 24px;
  width: auto;
}

/* 男性图标底纹样式 */
.gender-icon-with-number:first-child .gender-icon {
  background-color: #3c6b8e;
  border-radius: 50%;
  padding: 5px;
}

/* 女性图标底纹样式 */
.gender-icon-with-number:last-child .gender-icon {
  background-color: #3c6b8e;
  border-radius: 50%;
  padding: 5px;
}

/* 女性图标容器样式 */
.gender-icon-with-number:last-child {
  margin-left: 10px;
}

/* 图标和数字容器样式 */
.gender-icon-with-number {
  display: flex;
  align-items: center;
}

/* 男性图标容器样式 */
.gender-icon-with-number:first-child {
  margin-right: 0px;
}

/* 性别数字样式 */
.gender-number {
  color: #ffffff;
  font-size: 18px;
  margin-left: 5px;
  font-weight: bold;
}

/* 男性数字后的"人"字样式 */
.gender-icon-with-number:first-child .gender-number::after {
  content: "人";
  color: #ffffff;
  margin-left: 2px;
  display: inline-block;
  vertical-align: baseline;
}

/* 女性数字后的"人"字样式 */
.gender-icon-with-number:last-child .gender-number::after {
  content: "人";
  color: #ffffff;
  margin-left: 2px;
  display: inline-block;
  vertical-align: baseline;
}

.sub-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* 正方形容器内容样式 */
.square-content {
  flex: 1;
  font-size: 10px;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: 2px;
  position: relative;
}

/* 填满剩余空间的容器样式 */
.fill-remaining-space {
  flex: 1;
  min-height: 100px;
  border: none;
  border-radius: 4px;
  background: #000000;
  margin-top: -20px;
}

/* 正方形容器内容区域的三分之一横线 */
.square-content::after {
  display: none;
}

.nested-top {
  flex: 1;
  background: #000000;
  border-radius: 3px;
  margin-bottom: 3px;
  padding: 6px;
  color: #fff;
  font-size: 11px;
}

/* nested-top内的chart-row样式 */
.nested-top .chart-row {
  display: flex;
  gap: 10px;
  height: 100%;
  position: relative;
}

/* nested-top内chart-row的竖线分隔 */
.nested-top .chart-row::after {
  display: none;
}

/* nested-top内的chart-container样式 */
.nested-top .chart-container {
  flex: 1;
  border-radius: 3px;
  background: #000000;
  padding: 6px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nested-bottom {
  flex: 2;
  background: #000000;
  border-radius: 3px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
}

.side-by-side-containers .sub-container:hover {
  background-color: rgba(255, 255, 255, 0.15);
}

/* 自定义滚动条 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

::-webkit-scrollbar-thumb {
  background: rgba(24, 144, 255, 0.5);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(24, 144, 255, 0.7);
}

/* 垂直子容器容器样式 */
.vertical-sub-containers {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
}

/* 单个垂直子容器样式 */
.vertical-sub-container {
  flex: 1;
  background: #000000;
  border-radius: 4px;
  padding: 8px;
  display: flex;
  flex-direction: column;
}

/* 垂直子容器标题样式 */
.vertical-sub-title {
  font-size: 12px;
  color: white;
  margin-bottom: 4px;
  font-weight: bold;
}

/* 垂直子容器内容样式 */
.vertical-sub-content {
  flex: 1;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

/* 水平子容器容器样式 */
.horizontal-sub-containers {
  display: flex;
  gap: 6px;
  width: 100%;
  height: 100%;
}

/* 单个水平子容器样式 */
.horizontal-sub-container {
  flex: 1;
  background: #000000;
  border-radius: 3px;
  padding: 6px;
  display: flex;
  flex-direction: column;
}

/* 水平子容器标题样式 */
.horizontal-sub-title {
  font-size: 10px;
  color: white;
  margin-bottom: 3px;
  font-weight: bold;
  text-align: center;
}

/* 水平子容器内容样式 */
.horizontal-sub-content {
  flex: 1;
  font-size: 9px;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

/* 嵌套垂直子容器容器样式 */
.nested-vertical-containers {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  height: 100%;
}

/* 单个嵌套垂直子容器样式 */
.nested-vertical-container {
  flex: 1;
  background: #000000;
  border: none;
  border-radius: 2px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* 嵌套垂直子容器之间的红线分隔 - 已移除 */
/* .nested-vertical-container:not(:last-child)::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 10%;
  right: 10%;
  height: 1px;
  background: linear-gradient(to right, 
    transparent 0%, 
    rgba(255, 0, 0, 0.8) 20%, 
    rgba(255, 0, 0, 0.8) 80%, 
    transparent 100%
  );
  box-shadow: 0 0 3px rgba(255, 0, 0, 0.5);
} */

/* 嵌套垂直子容器标题样式 */
.nested-vertical-title {
  font-size: 8px;
  color: white;
  margin-bottom: 2px;
  font-weight: bold;
  text-align: center;
}

/* 嵌套垂直子容器内容样式 */
.nested-vertical-content {
  flex: 1;
  font-size: 7px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

/* 子容器内容样式 */
.sub-content {
  display: flex;              /* 使用flex布局，让内容在子容器内自动排列 */
  flex-direction: column;     /* 子元素垂直排列（从上到下） */
  height: 100%;               /* 高度填满父容器，保证内容撑满 */
  position: relative;         /* 相对定位，便于后代元素绝对定位 */
  overflow: visible;          /* 内容溢出时不裁剪，允许内容超出容器显示 */
}

/* 上部分样式 */
.top-section {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  position: relative;
  min-height: 80px;
}

/* 统一容器样式 */
.unified-container {
  display: flex;
  width: 100%;
  gap: 0;
  height: 80px; /* 减小高度，使容器整体高度进一步缩小 */
  margin-top: 15px; /* 添加上边距，与上边的"党群建设"底纹容器保持间隙 */
}

/* 统一容器内子元素样式 */
.unified-container > div {
  margin: 0;
  padding: 10px;
}

/* 第一个子元素样式 */
.unified-container > div:first-child {
  border-radius: 0;
  border-right: none;
}

/* 第二个子元素样式 */
.unified-container > div:last-child {
  border-radius: 0;
  border-left: none;
}

/* 数据标题样式 */
.data-title {
  font-size: 14px;
  color: white;
  margin-bottom: 0px;
  font-weight: bold;
  text-align: center;
}

/* 数据数值样式 */
.data-value {
  font-size: 28px;
  color: white;
  font-weight: bold;
  text-align: center;
  margin-bottom: 4px;
}

/* 数据单位样式 */
.data-unit {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
}

/* 下部分样式 */
.bottom-section {
  flex: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  overflow: visible;
}

/* 标题框样式 */
.title-box {
  width: 200%;
  height: 95%;
  display: flex;
  flex-direction: column;
  transform: translateY(-35px) scale(0.95);
  overflow: visible;
  border-bottom: none;
  padding-bottom: -5;
}

/* 社区议事部分的标题框样式调整 */
.right-column .title-box {
  transform: translateY(-25px) scale(0.95);
}

/* 标题框头部样式 */
.title-box-header {
  padding: 8px 12px;
  color: white;
  font-size: 14px;
  font-weight: bold;
  text-align: center;
  border-bottom: none;
}

/* 标题框内容样式 */
.title-box-content {
  flex: 1;
  padding: 12px;
  padding-top: 6px;
  color: white;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: visible;
}

/* 活动项样式 */
.activity-item {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: white;
  line-height: 1.6;
  padding: 6px 0;
  min-height: 24px;
  background: #000000;
  border-radius: 6px;
}

/* 统计网格样式 */
.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 8px;
  margin-top: 10px;
  padding: 10px;
  flex: 1;
}

/* 统计项样式 */
.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1px 8px;
}

/* 需要向上移动的统计项样式 */
.stat-item-top {
  padding: 0px 8px !important;
}

/* 社区网格数和社区网格员数向上移动样式 */
.stat-item-top-first {
  padding: 0px 8px !important;
}

/* 统计标题样式 */
.stat-title {
  font-size: 12px;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 10px;
  text-align: center;
}

/* 统计数值样式 */
.stat-value {
  font-size: 12px;
  font-weight: bold;
  color:#17dff0;
  text-align: center;
  margin-bottom:30px;
}



.mediation-event-pie-chart,
.golden-idea-pie-chart,
.key-population-chart {
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 150px;
}

/* 重点人群分析整体容器边框样式 */
.key-population-container {
  border-radius: 4px;
  background: #000000;
  padding: 8px;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
}

/* 重点人群分析容器内的标题样式调整 */
.key-population-container .chart-title {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: none;
}

/* 基层队伍网格容器样式 */


/* 党群建设和社区议事模块边框样式 */
.sub-container.no-sub-border.no-background {
  background: #000000;
}

/* 全屏按钮样式
   让全屏按钮固定在页面右上角，始终可见，且鼠标悬停时变成小手。
*/
.fullscreen-btn {
  position: fixed;  /* 固定定位，让按钮始终在页面固定位置，不随滚动条移动 */
  top: 10px;        /* 距离页面顶部10像素 */
  right: 10px;      /* 距离页面右侧10像素 */
  z-index: 9999;    /* 层级很高，保证按钮不会被其他内容遮挡 */
  cursor: pointer;  /* 鼠标悬停时变成小手，提示可点击 */
}

/* 全屏按钮容器样式
   .fullscreen-btn 是外层按钮区域，.screenfull-container 是内部的全屏按钮容器。
   下面的样式让里面的内容横向排列，并且每个子元素之间有8px的间距。
*/
.fullscreen-btn .screenfull-container {
  display: flex; /* 让子元素横向排列（弹性盒布局） */
  gap: 8px;      /* 子元素之间的间距为8像素 */
}

/* 全屏状态下的样式调整 */
.wisdom-community-screen.fullscreen {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.wisdom-community-screen.fullscreen .main-content {
  width: 100%;
  height: 100%;
}

.wisdom-community-screen.fullscreen .bordered-section {
  border-radius: 0;
}

.wisdom-community-screen.fullscreen .chart {
  width: 100% !important;
  height: 100% !important;
}

/* 全屏状态下左侧容器及其内部元素的样式调整 */
.wisdom-community-screen.fullscreen .left-section {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.wisdom-community-screen.fullscreen .charts-container {
  flex: 1;
  margin-bottom: 10px;
  height: auto;
}

.wisdom-community-screen.fullscreen .left-section .bordered-section:nth-child(2) {
  flex: 2;
  height: auto;
  position: relative;
}

/* 全屏状态下基层队伍容器的样式调整 - 不再固定大小，跟随 flex */
.wisdom-community-screen.fullscreen .team-section {
  flex: 1 !important;
  min-height: 0 !important;
  height: auto !important;
}

/* 元素全屏状态下的样式调整 - 确保容器位置不变 */
.wisdom-community-screen:fullscreen {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: #000;
}

.wisdom-community-screen:fullscreen .main-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
}

.wisdom-community-screen:fullscreen .left-section,
.wisdom-community-screen:fullscreen .middle-section,
.wisdom-community-screen:fullscreen .right-section {
  height: 100%;
  overflow: hidden;
}

.wisdom-community-screen:fullscreen .chart {
  width: 100% !important;
  height: 100% !important;
}
</style>
